import React, { useState, useEffect, useRef, useMemo } from 'react';
import { doc, getDoc, updateDoc, addDoc, deleteDoc, collection, writeBatch, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useRole } from '../hooks/useRole';
import { useData } from '../context/DataContext';
import TemplatesPickerModal from '../components/TemplatesPickerModal';
import LineItemEditorModal from '../components/LineItemEditorModal';
import ProductPickerModal from '../components/ProductPickerModal';
import EstimatorMenu from '../components/EstimatorMenu';
import {
  getPaymentStatus, sumPayments, PAYMENT_STATUS, paymentMirrorFields, fetchInvoicePayments,
} from '../utils/paymentStatus';
import { generateDocNumber } from '../utils/docNumber';
import { convertQuoteToInvoice } from '../utils/convertQuote';
import {
  lineTotal, lineCost, computeDocTotals, round2, buildCostLookup, resolveUnitCost,
} from '../utils/lineMath';
import {
  isOnPlate, setLineOnPlate, editLineField, setLineBasePrice, reconcileEditedLine, ON_PLATE_SURCHARGE,
} from '../utils/onPlate';
import { roundLineItems } from '../utils/packageRounding';
import {
  readEstimates, applyEstimatorImport, estimatorImportKey,
  estimatorSpecsMirror, pendingModify, removeEstimate, PENDING_ADD,
} from '../utils/estimates';
import { EstimateModifyControl, EstimatesList } from '../components/EstimatesPanel';
import { getAllPhoneNumbers } from '../utils/customerPhone';
import { reserveCustomerNumber } from '../utils/customerNumber';
import { BOARD_STATUS_OPTIONS } from '../utils/boardColumns';
import CreditHoldWarning from '../components/CreditHoldWarning';
import {
  getDeductableQuantities,
  computeInventoryDelta,
  describeDelta,
  applyInventoryDelta,
  buildUpdatedLedger,
  getEffectiveLedger,
  resolveInventoryStatus,
} from '../utils/inventoryLedger';

const CATEGORIES = [
  'Chain Link',
  'Galv Fittings',
  'Black Fittings',
  'Galv Pipe',
  'Black Pipe',
  'Residential Swing Gates Galv',
  'Commercial Swing Gates Galv',
  'Residential Gates Black',
  'Commercial Gates Black',
  'Wood',
  'Wood Hardware',
  'Split Rail',
  'Tools/Misc',
  'PVC',
  'Cantilever Gate',
  'Aluminum Slide Gate Hardware',
  'Aluminum',
];

// Draft persistence for round-trips through the Estimator (which would otherwise
// unmount this component and lose unsaved edits). Scoped per docType+id so editing
// two different documents in succession doesn't cross-contaminate.
const draftKey = (docType, id) => `docDraft_edit_${docType}_${id}`;
const saveDraft = (docType, id, data) => {
  try { sessionStorage.setItem(draftKey(docType, id), JSON.stringify(data)); } catch {}
};
const loadDraft = (docType, id) => {
  try {
    const raw = sessionStorage.getItem(draftKey(docType, id));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const clearDraft = (docType, id) => {
  try { sessionStorage.removeItem(draftKey(docType, id)); } catch {}
};



// Inventory alert status for a product being added to a document. Returns null
// when no alert is warranted (untracked product, or sufficient stock).
const getStockStatus = (product, requestedQty = 1) => {
  if (!product || !product.trackInventory) return null;
  const stock = parseFloat(product.stockQty) || 0;
  const reorder = parseFloat(product.reorderPoint) || 0;
  if (stock <= 0) return { level: 'out', message: 'Out of Stock', stockQty: stock, requestedQty };
  if (stock < requestedQty) return { level: 'insufficient', message: 'Not Enough Stock', stockQty: stock, requestedQty };
  if (stock <= reorder) return { level: 'low', message: 'Low Stock', stockQty: stock, requestedQty };
  return null;
};

function EditDocumentInner({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { type, id } = useParams();
  const { isAdmin, isSalesperson, userName: roleName, loading: roleLoading } = useRole();
  const userName = roleName || user?.email?.split('@')[0]?.toUpperCase();
  const docType = type === 'invoice' ? 'invoice' : 'quote';

  const { products, customers, updateProduct, addCustomerToCache } = useData();

  const [loadingDoc, setLoadingDoc] = useState(true);
  const [originalDoc, setOriginalDoc] = useState(null);
  const [originalLineItems, setOriginalLineItems] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // Per-document tax-exempt toggle. On edit it initializes from the DOCUMENT's
  // stored taxExempt; on customer change it re-inherits the new customer's flag
  // (see the inherit effect). A manual toggle overrides it for THIS document only.
  const [docTaxExempt, setDocTaxExempt] = useState(false);
  const prevCustIdRef = useRef(undefined);
  const canInheritTaxExemptRef = useRef(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '', phone: '', email: '', address: '', notes: '', taxExempt: false,
  });
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [possibleDuplicates, setPossibleDuplicates] = useState([]);
  const [pendingCustomerData, setPendingCustomerData] = useState(null);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [priceLevel, setPriceLevel] = useState('A');
  const [lineItems, setLineItems] = useState([]);
  // Every estimate on this document: [{ id, kind, specs, label }]. Lines carry
  // the estimateId of the estimate they came from. See utils/estimates.js.
  const [estimates, setEstimates] = useState([]);
  // Fingerprints of estimator returns already applied to this document's draft,
  // so revisiting the same return cannot import it twice.
  const appliedImportKeysRef = useRef([]);
  const [productSearch, setProductSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [deliveryFee, setDeliveryFee] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountEnabled, setDiscountEnabled] = useState(false);
  const [isDelivery, setIsDelivery] = useState(false);
  const [deliveryStreet, setDeliveryStreet] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryState, setDeliveryState] = useState('');
  const [deliveryZip, setDeliveryZip] = useState('');
  const [notes, setNotes] = useState('');
  const [docNumber, setDocNumber] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [converting, setConverting] = useState(false);
  const [showNonStockForm, setShowNonStockForm] = useState(false);
  const [nonStockForm, setNonStockForm] = useState({ description: '', qty: 1, unit: 'pc', unitPrice: '', cost: '' });
  const [showTemplatesPicker, setShowTemplatesPicker] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showItemEditor, setShowItemEditor] = useState(false);
  // Manual line-item reordering: single-click selects a row (tracked by stable id),
  // ▲▼ buttons swap it with its neighbor in the array (array order = save/PDF order).
  const [selectedLineId, setSelectedLineId] = useState(null);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [showOfficeUse, setShowOfficeUse] = useState(false);
  // Inventory alerts when adding low/out-of-stock products
  const [stockAlertProduct, setStockAlertProduct] = useState(null);
  const [stockAlertStatus, setStockAlertStatus] = useState(null);
  const [batchStockAlerts, setBatchStockAlerts] = useState([]);
  const [pendingBatchProducts, setPendingBatchProducts] = useState(null);
  // Downgrade invoice → quote
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [downgradeStep, setDowngradeStep] = useState('confirm'); // 'confirm' | 'inventory' | 'processing'
  const [downgradeInventoryItems, setDowngradeInventoryItems] = useState([]);
  // Product costs, indexed by id AND by SKU. The two maps this replaced were
  // built here; they now come from buildCostLookup in utils/lineMath.js so the
  // line item editor indexes them identically. The SKU index is what recovers a
  // cost when a destructive re-import has orphaned a line's productId.
  // Memoized for the same reason as before: it is read once per line item.
  const costLookup = useMemo(() => buildCostLookup(products), [products]);

  // Payment state (invoice only)
  const [payments, setPayments] = useState([]);
  // True once `payments` holds this invoice's records as read from the payments
  // collection. Until then the status on screen is UNKNOWN, not "unpaid".
  const [paymentsLoaded, setPaymentsLoaded] = useState(false);
  // True when the last attempt to read them failed — shown on screen, not only
  // logged. handleSave reads them again itself and refuses to save if it can't.
  const [paymentsError, setPaymentsError] = useState(false);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [boardStatus, setBoardStatus] = useState('Ordered');
  const mountedRef = useRef(false);
  const docLoadedRef = useRef(false);
  // Snapshot of the editable fields as of the loaded/last-saved state. The
  // change-tracking effect flags the doc dirty only when current values differ
  // from this baseline — order-independent, so async load writes never count as
  // a user edit (fixes false "unsaved changes" on a fresh open).
  const baselineRef = useRef(null);

  const TAX_RATE = 0.06;

  // Close product dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.product-search-container')) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Wait for products before loading: the document's items are mapped to
  // products (SKU → productId) and estimator items are priced against the
  // products list, so loading before products are ready yields wrong prices.
  useEffect(() => {
    if (docLoadedRef.current) return;
    if (!products || products.length === 0) return;
    docLoadedRef.current = true;
    loadExistingDocument();
  }, [products]);

  // Block salesperson from editing another salesperson's document
  useEffect(() => {
    if (!loadingDoc && !roleLoading && originalDoc && isSalesperson) {
      if (originalDoc.salesperson && originalDoc.salesperson !== userName) {
        alert("You can only edit your own documents.");
        navigate('/documents');
      }
    }
  }, [loadingDoc, roleLoading, originalDoc, isSalesperson, userName, navigate]);

  const loadExistingDocument = async () => {
    setLoadingDoc(true);
    try {
      // Always fetch from Firestore — needed for originalDoc/originalLineItems
      // (used by inventory diff) even when a draft will overlay the editable fields.
      const col = docType === 'quote' ? 'quotes' : 'invoices';
      const snap = await getDoc(doc(db, col, id));
      if (!snap.exists()) {
        alert('Document not found.');
        navigate('/documents');
        return;
      }
      const data = snap.data();
      const loadedItems = (data.lineItems || []).map(item => {
        if (!item.productId && item.sku && item.sku !== '—' && item.sku !== 'NON-STOCK') {
          const product = products.find(p => p.sku === item.sku && p.isActive);
          if (product) return { ...item, productId: product.id };
        }
        return item;
      });
      setOriginalDoc(data);
      setOriginalLineItems(JSON.parse(JSON.stringify(loadedItems)));

      // If a draft exists for this document (saved before navigating to the estimator),
      // restore the editable fields from it instead of Firestore.
      const draft = loadDraft(docType, id);
      let effectiveLevel = 'A';
      // Lines and estimates as restored — from the draft or the stored document —
      // in the new shape. An older document's single estimatorSpecs is converted
      // here, in memory only; the next save writes the new shape.
      let restored = { lineItems: [], estimates: [] };
      if (draft) {
        setSelectedCustomer(draft.selectedCustomer || null);
        // Seed the customer-change ref so the draft restore's initial customer set is
        // not treated as a CHANGE (keeps the restored priceLevel; no re-inherit/re-price).
        prevCustIdRef.current = draft.selectedCustomer?.id;
        // Restore the document's tax-exempt toggle (falls back to the stored doc
        // value, then the customer's flag, for older drafts). The inherit effect is
        // gated off during init, so a manual toggle survives the estimator round-trip.
        setDocTaxExempt(draft.docTaxExempt ?? data.taxExempt ?? !!draft.selectedCustomer?.taxExempt);
        // A draft saved before this change holds estimatorSpecs, not estimates.
        restored = readEstimates({ estimates: draft.estimates, estimatorSpecs: draft.estimatorSpecs, lineItems: draft.lineItems || [] });
        setLineItems(restored.lineItems);
        setEstimates(restored.estimates);
        effectiveLevel = draft.priceLevel || 'A';
        setPriceLevel(effectiveLevel);
        setNotes(draft.notes || '');
        setPoNumber(draft.poNumber || '');
        setIsDelivery(!!draft.isDelivery);
        setDeliveryFee(draft.deliveryFee || '');
        setDiscountPercent(draft.discountPercent ?? data.discountPercent ?? 0);
        setDiscountEnabled(draft.discountEnabled ?? ((draft.discountPercent ?? data.discountPercent ?? 0) > 0));
        setDeliveryStreet(draft.deliveryStreet || '');
        setDeliveryCity(draft.deliveryCity || '');
        setDeliveryState(draft.deliveryState || '');
        setDeliveryZip(draft.deliveryZip || '');
        setDocNumber(draft.docNumber || data.docNumber || '');
        if (docType === 'invoice') {
          setPayments(draft.payments || []);
          setBoardStatus(draft.boardStatus || 'Ordered');
        }
      } else {
        setDocNumber(data.docNumber || '');
        effectiveLevel = data.priceLevel || 'A';
        setPriceLevel(effectiveLevel);
        restored = readEstimates({ estimates: data.estimates, estimatorSpecs: data.estimatorSpecs, lineItems: loadedItems });
        setLineItems(restored.lineItems);
        setEstimates(restored.estimates);
        setNotes(data.notes || '');
        setPoNumber(data.poNumber || '');
        if (docType === 'invoice') setBoardStatus(data.status || 'Ordered');
        setIsDelivery(data.isDelivery || false);
        setDeliveryFee(data.deliveryFee ? String(data.deliveryFee) : '');
        setDiscountPercent(data.discountPercent || 0);
        setDiscountEnabled((data.discountPercent || 0) > 0);
        setDeliveryStreet(data.deliveryStreet || '');
        setDeliveryCity(data.deliveryCity || '');
        setDeliveryState(data.deliveryState || '');
        setDeliveryZip(data.deliveryZip || '');
        setSelectedCustomer({
          id: data.customerId || '',
          name: data.customerName || '',
          address: data.customerAddress || '',
          customerNumber: data.customerNumber || '',
          taxExempt: data.taxExempt || false,
          phone: data.customerPhone || '',
        });
        // Seed the customer-change ref with the loaded customer id so the inherit
        // effect does NOT treat this initial load as a customer CHANGE. Without this,
        // the effect would run handlePriceLevelChange(customer.defaultPriceLevel || 'A')
        // — and the loaded customer object has no defaultPriceLevel, so it would
        // clobber the doc's saved priceLevel to 'A' and re-price items. Deterministic
        // (independent of the setTimeout gate race). A later real customer change has a
        // different id, so the effect still re-inherits then.
        prevCustIdRef.current = data.customerId || '';
        // Initialize the per-document toggle from the DOCUMENT's stored flag, so an
        // old exempt/taxed doc keeps its own status regardless of the customer's
        // current flag. The inherit effect is gated off during init (won't override).
        setDocTaxExempt(data.taxExempt || false);
      }

      // Payments are loaded from the canonical top-level `payments` collection
      // (the same source ViewDocument reads via where('invoiceId','==',id)), NOT
      // from the invoice's embedded `payments` array. This makes Edit show the
      // same total/status as the View screen. It runs after the invoice doc is
      // loaded (so we have its id) and intentionally OVERRIDES any draft-restored
      // payments above — a draft only carries unsaved edits to OTHER fields,
      // while payments must reflect the saved collection. Quotes have none.
      if (docType === 'invoice') {
        try {
          setPayments(await fetchInvoicePayments(id));
          setPaymentsLoaded(true);
          setPaymentsError(false);
        } catch (err) {
          // Visible, not only logged: the payment panel says the records could
          // not be read. Saving does not rely on this read — handleSave reads
          // them again itself, and blocks the save if that fails too.
          console.error('Error loading payments:', err);
          setPaymentsError(true);
        }
      }

      // If returning from an estimator with a new calculation, apply it on top of
      // whatever was just restored — through the ONE shared import in
      // utils/estimates.js. The draft saved before the trip says whether it was
      // to add a new estimate or modify an existing one.
      appliedImportKeysRef.current = (draft && draft.appliedImportKeys) || [];
      // The SAME condition NewDocument uses. This page used to also require
      // estimatorSpecs, so lines arriving without settings were silently
      // ignored here but imported (unattached) there. The shared import already
      // handles that case, so both pages now accept it the same way.
      if (
        location.state?.lineItems &&
        location.state.lineItems.length > 0 &&
        !location.state?.keepCurrentItems
      ) {
        const importKey = estimatorImportKey(location.state.lineItems);
        if (!(importKey && appliedImportKeysRef.current.includes(importKey))) {
          const result = applyEstimatorImport({
            lineItems: restored.lineItems,
            estimates: restored.estimates,
            incomingItems: location.state.lineItems,
            incomingSpecs: location.state?.estimatorSpecs || null,
            pending: draft ? draft.pendingEstimate : null,
            products,
            // Re-priced at THIS document's level; the estimator computes at A.
            priceLevel: location.state?.priceLevel || effectiveLevel,
            priceForLevel: getPriceForLevel,
          });
          setLineItems(result.lineItems);
          setEstimates(result.estimates);
          if (importKey) appliedImportKeysRef.current = [...appliedImportKeysRef.current, importKey];
          setTimeout(() => setHasUnsavedChanges(true), 0);
        }
      }
    } catch (err) {
      console.error('Error loading document:', err);
      alert('Error loading document.');
    }
    setLoadingDoc(false);
    setTimeout(() => { mountedRef.current = true; }, 0);
    // Enable customer-change inheritance only AFTER load settles (one tick), so the
    // seeded docTaxExempt (from the stored doc or draft) isn't overwritten by the
    // initial customer set. Mirrors the mountedRef settle above.
    setTimeout(() => { canInheritTaxExemptRef.current = true; }, 0);
  };

  // Inherit the customer's taxExempt AND default price level onto the document
  // whenever the selected customer actually CHANGES (fresh inheritance). Gated by
  // canInheritTaxExemptRef so the initial edit-load / draft-restore (which seed
  // these from the DOCUMENT) win — the saved priceLevel is NOT overridden on load;
  // only a user-initiated customer change re-inherits. Price level uses
  // handlePriceLevelChange so line items re-price (manual/custom/override preserved).
  useEffect(() => {
    const id = selectedCustomer?.id;
    if (!canInheritTaxExemptRef.current) {
      prevCustIdRef.current = id;   // track during init, do not inherit
      return;
    }
    if (prevCustIdRef.current !== id) {
      prevCustIdRef.current = id;
      setDocTaxExempt(!!selectedCustomer?.taxExempt);
      handlePriceLevelChange(selectedCustomer?.defaultPriceLevel || 'A');
    }
  }, [selectedCustomer]);

  // `opts.pendingEstimate` records what an estimator trip is for — see
  // utils/estimates.js. Anything else (or nothing) is an add, which can never
  // remove lines.
  const saveCurrentDraft = (opts) => {
    saveDraft(docType, id, {
      selectedCustomer, docTaxExempt, lineItems, estimates,
      pendingEstimate: (opts && opts.pendingEstimate) || PENDING_ADD,
      appliedImportKeys: appliedImportKeysRef.current,
      priceLevel, notes, poNumber, isDelivery,
      deliveryFee, discountPercent, discountEnabled, deliveryStreet, deliveryCity, deliveryState, deliveryZip,
      docNumber,
      ...(docType === 'invoice' ? { payments, boardStatus } : {}),
    });
  };

  const getPriceForLevel = (product, level) => {
    if (level === 'A') return product.priceA || 0;
    if (level === 'B') return product.priceB || 0;
    if (level === 'C') return product.priceC || 0;
    return product.priceA || 0;
  };

  // ── Quick-create customer (without leaving the document) ──
  const openNewCustomerModal = () => {
    setNewCustomerData({
      name: customerSearch.trim(), phone: '', email: '', address: '', notes: '', taxExempt: false,
    });
    setShowNewCustomerModal(true);
  };

  // ── Duplicate detection ──
  const normalize = (str) => (str || '').toLowerCase().trim().replace(/\s+/g, ' ');
  const normalizePhone = (phone) => (phone || '').replace(/\D/g, '');

  const findPossibleDuplicates = (newData) => {
    const newName = normalize(newData.name);
    const newPhone = normalizePhone(newData.phone);
    const newEmail = normalize(newData.email);
    if (!newName) return [];

    const matches = [];
    customers.forEach(existing => {
      const existingName = normalize(existing.name);
      // ALL of this customer's numbers, digits-only — a duplicate is a duplicate
      // whether the number matches their primary or any secondary. Still EXACT
      // equality per number (not substring): a partial number must not collapse
      // unrelated customers into a false duplicate.
      const existingPhones = getAllPhoneNumbers(existing).map(normalizePhone).filter(Boolean);
      const phoneMatches = !!newPhone && existingPhones.includes(newPhone);
      const existingEmail = normalize(existing.email);

      let matchScore = 0;
      let matchType = null;

      if (existingName === newName) {
        matchScore = 100;
        matchType = 'exact-name';
      } else if (newPhone || newEmail) {
        // Similar name only counts when phone or email also cross-matches.
        const words1 = new Set(existingName.split(' ').filter(w => w.length > 1));
        const words2 = new Set(newName.split(' ').filter(w => w.length > 1));
        let shared = 0;
        words2.forEach(w => { if (words1.has(w)) shared++; });
        const minWords = Math.min(words1.size, words2.size);
        const sharedRatio = minWords > 0 ? shared / minWords : 0;
        if (sharedRatio >= 0.5 && shared >= 1) {
          if (phoneMatches) {
            matchScore = 90;
            matchType = 'similar-name-same-phone';
          } else if (newEmail && existingEmail && newEmail === existingEmail) {
            matchScore = 85;
            matchType = 'similar-name-same-email';
          }
        }
      }

      // Same phone, different name — possibly the same person.
      if (matchScore === 0 && phoneMatches) {
        matchScore = 70;
        matchType = 'same-phone';
      }

      if (matchScore > 0) matches.push({ ...existing, matchScore, matchType });
    });

    return matches.sort((a, b) => b.matchScore - a.matchScore).slice(0, 5);
  };

  const resetNewCustomerFlow = () => {
    setShowNewCustomerModal(false);
    setShowDuplicateWarning(false);
    setPendingCustomerData(null);
    setPossibleDuplicates([]);
    setNewCustomerData({ name: '', phone: '', email: '', address: '', notes: '', taxExempt: false });
  };

  const handleCreateAndSelectCustomer = async () => {
    if (!newCustomerData.name.trim()) { alert('Customer name is required.'); return; }
    const duplicates = findPossibleDuplicates(newCustomerData);
    if (duplicates.length > 0) {
      setPossibleDuplicates(duplicates);
      setPendingCustomerData({ ...newCustomerData });
      setShowDuplicateWarning(true);
      return; // wait for the user to decide
    }
    await createCustomer(newCustomerData);
  };

  // Actual creation, extracted so the duplicate modal can call it too.
  const createCustomer = async (customerData) => {
    setCreatingCustomer(true);
    try {
      const customerNumber = await reserveCustomerNumber(customers);
      const customerToCreate = {
        name: customerData.name.trim(),
        phone: customerData.phone.trim(),
        email: customerData.email.trim(),
        address: customerData.address.trim(),
        notes: customerData.notes.trim(),
        taxExempt: customerData.taxExempt,
        customerNumber,
        createdAt: new Date().toISOString(),
      };
      const docRef = await addDoc(collection(db, 'customers'), customerToCreate);
      const newCustomer = { id: docRef.id, ...customerToCreate };
      addCustomerToCache(newCustomer);
      // Auto-select for this document — without touching any other state, so all
      // line items, price level, delivery, notes, etc. are preserved.
      setSelectedCustomer(newCustomer);
      setCustomerSearch('');
      setHasUnsavedChanges(true);
      resetNewCustomerFlow();
    } catch (err) {
      console.error('Error creating customer:', err);
      alert('Error creating customer: ' + err.message);
    }
    setCreatingCustomer(false);
  };

  const selectExistingCustomer = (existingCustomer) => {
    setSelectedCustomer(existingCustomer);
    setCustomerSearch('');
    setHasUnsavedChanges(true);
    resetNewCustomerFlow();
  };

  const createAnyway = async () => {
    if (pendingCustomerData) await createCustomer(pendingCustomerData);
  };

  // Small inventory badge for product rows (🔴 OUT / 🟡 LOW). Returns null when fine.
  const stockBadge = (product) => {
    if (!product?.trackInventory) return null;
    const stock = parseFloat(product.stockQty) || 0;
    const reorder = parseFloat(product.reorderPoint) || 0;
    if (stock <= 0) return <span className="ml-2 bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-bold whitespace-nowrap">🔴 OUT</span>;
    if (stock <= reorder) return <span className="ml-2 bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs font-bold whitespace-nowrap">🟡 LOW ({stock})</span>;
    return null;
  };

  const addLineItem = (product) => {
    const status = getStockStatus(product, 1);
    if (status) {
      setStockAlertProduct(product);
      setStockAlertStatus(status);
      setShowProductDropdown(false);
      return;
    }
    commitLineItem(product);
  };

  const commitLineItem = (product) => {
    setLineItems(prev => [...prev, {
      id: Date.now() + Math.random(),
      sku: product.sku,
      description: product.description,
      unit: product.unit,
      qty: 1,
      unitPrice: getPriceForLevel(product, priceLevel),
      isStock: product.isStock,
      productId: product.id,
      fromEstimator: false,
    }]);
    setHasUnsavedChanges(true);
    setProductSearch('');
    setShowProductDropdown(false);
  };

  const updateLineItem = (itemId, field, value) => {
    setLineItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      // Through the shared helper: on an ON PLATE line the typed description and
      // price are the DISPLAYED ones, so the base values behind them are kept in
      // step. On every other line this is the plain field set it always was.
      const updated = editLineField(item, field, value);
      // Mark items as manually-priced so price-level changes don't overwrite the user's value.
      if (field === 'unitPrice') updated.manualPrice = true;
      return updated;
    }));
  };

  // ── ON PLATE ──
  // Appends " ON PLATE" to the description and adds $50 to the UNIT price (so
  // the quantity multiplies it). Unticking restores the exact original text and
  // price — see utils/onPlate.js. Nothing here touches inventory: a plate is not
  // deducted as a product.
  const toggleOnPlate = (itemId, checked) => {
    setLineItems(prev => prev.map(item => (item.id === itemId ? setLineOnPlate(item, checked) : item)));
    setHasUnsavedChanges(true);
  };

  const addProductsFromPicker = (selectedProducts) => {
    if (!selectedProducts || selectedProducts.length === 0) return;
    const withAlerts = selectedProducts
      .map(p => ({ product: p, status: getStockStatus(p, 1) }))
      .filter(item => item.status !== null);
    if (withAlerts.length > 0) {
      // Surface a consolidated alert over the still-open picker; remember the
      // full selection so "Add All Anyway" adds OK items too.
      setBatchStockAlerts(withAlerts);
      setPendingBatchProducts(selectedProducts);
      return;
    }
    commitProductsFromPicker(selectedProducts);
  };

  const commitProductsFromPicker = (selectedProducts) => {
    if (!selectedProducts || selectedProducts.length === 0) return;
    const baseId = Date.now();
    const newItems = selectedProducts.map((product, index) => ({
      id: baseId + index + Math.random(),
      sku: product.sku,
      description: product.description,
      unit: product.unit || 'pc',
      qty: 1,
      unitPrice: getPriceForLevel(product, priceLevel),
      isStock: product.isStock ?? true,
      productId: product.id,
      fromEstimator: false,
    }));
    setLineItems(prev => [...prev, ...newItems]);
    setHasUnsavedChanges(true);
    setShowProductPicker(false);
    setBatchStockAlerts([]);
    setPendingBatchProducts(null);
  };

  const resetItemPrice = (itemId) => {
    setLineItems(prev => prev.map(item => {
      if (item.id !== itemId) return item;
      const product = products.find(p => p.id === item.productId) ||
        (item.sku && item.sku !== '—' && item.sku !== 'NON-STOCK' && item.sku !== 'TEMPLATE'
          ? products.find(p => p.sku === item.sku && p.isActive)
          : null);
      if (!product) return { ...item, manualPrice: false, itemPriceLevel: null };
      // setLineBasePrice, not a raw unitPrice write: on an ON PLATE line the
      // $50 is re-applied on top of the price-level price, so resetting can
      // never leave the line reading ON PLATE without the surcharge.
      return { ...setLineBasePrice(item, getPriceForLevel(product, priceLevel)), manualPrice: false, itemPriceLevel: null };
    }));
    setHasUnsavedChanges(true);
  };

  const openItemEditor = (item) => {
    if (item?.isTextNote) return; // text notes have no qty/price to edit
    setEditingItem(item);
    setShowItemEditor(true);
  };

  const saveItemEdits = (updatedItem) => {
    // The modal edits the DISPLAYED description and price and knows nothing
    // about plates, so the bases are re-derived from what it returns.
    setLineItems(prev => prev.map(item => item.id === updatedItem.id ? reconcileEditedLine({ ...updatedItem }) : item));
    setHasUnsavedChanges(true);
    setShowItemEditor(false);
    setEditingItem(null);
  };

  const removeLineItem = (itemId) =>
    setLineItems(prev => prev.filter(item => item.id !== itemId));

  // Move the selected line item up/down by swapping array positions. Selection is
  // tracked by id, so the highlight follows the item and ▲/▼ can be clicked repeatedly.
  const moveLineItem = (direction) => {
    setLineItems(prev => {
      const idx = prev.findIndex(it => it.id === selectedLineId);
      if (idx === -1) return prev;
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
    setHasUnsavedChanges(true);
  };

  const addTemplateBundleToDocument = (template) => {
    const items = template.items || [];
    if (items.length === 0) {
      alert('This template has no items.');
      return;
    }
    const bundleId = `bundle-${Date.now()}`;
    const baseId = Date.now();
    const newItems = items.map((item, index) => ({
      id: baseId + index + Math.random(),
      sku: 'TEMPLATE',
      description: item.description,
      unit: item.unit || 'pc',
      qty: parseFloat(item.defaultQty) || 1,
      unitPrice: parseFloat(item.defaultPrice) || 0,
      costPrice: parseFloat(item.defaultCost) || 0,
      isStock: false,
      productId: null,
      fromTemplate: true,
      templateBundleId: bundleId,
      templateBundleName: template.name,
      templateBundleOrder: index,
      fromEstimator: false,
    }));
    setLineItems(prev => [...prev, ...newItems]);
    setHasUnsavedChanges(true);
    setShowTemplatesPicker(false);
  };

  const addStockBundleToDocument = (bundle) => {
    const items = bundle.items || [];
    if (items.length === 0) {
      alert('This bundle has no items.');
      return;
    }
    const bundleId = `stockbundle-${Date.now()}`;
    const baseId = Date.now();
    const newItems = items.map((bundleItem, index) => {
      const product = products.find(p => p.id === bundleItem.productId);
      if (!product) {
        return {
          id: baseId + index + Math.random(),
          sku: bundleItem.sku || '—',
          description: `⚠️ NOT FOUND: ${bundleItem.description}`,
          unit: 'pc',
          qty: 1,
          unitPrice: 0,
          costPrice: 0,
          isStock: false,
          productId: null,
          fromStockBundle: true,
          stockBundleId: bundleId,
          stockBundleName: bundle.name,
          stockBundleOrder: index,
          fromEstimator: false,
        };
      }
      return {
        id: baseId + index + Math.random(),
        sku: product.sku,
        description: product.description,
        unit: product.unit || 'pc',
        qty: 1,
        unitPrice: getPriceForLevel(product, priceLevel),
        costPrice: parseFloat(product.costPrice) || 0,
        isStock: product.isStock ?? true,
        productId: product.id,
        fromStockBundle: true,
        stockBundleId: bundleId,
        stockBundleName: bundle.name,
        stockBundleOrder: index,
        fromEstimator: false,
      };
    });
    setLineItems(prev => [...prev, ...newItems]);
    setHasUnsavedChanges(true);
    setShowTemplatesPicker(false);
  };

  const confirmNonStockItem = () => {
    if (!nonStockForm.description.trim()) { alert('Please enter a description.'); return; }
    setLineItems(prev => [...prev, {
      id: Date.now(),
      sku: 'NON-STOCK',
      description: nonStockForm.description.trim(),
      unit: nonStockForm.unit,
      qty: parseFloat(nonStockForm.qty) || 1,
      unitPrice: parseFloat(nonStockForm.unitPrice) || 0,
      cost: parseFloat(nonStockForm.cost) || 0,
      isStock: false,
      isNonStockCustom: true,
      productId: null,
      fromEstimator: false,
    }]);
    setShowNonStockForm(false);
    setNonStockForm({ description: '', qty: 1, unit: 'pc', unitPrice: '', cost: '' });
  };

  const handlePriceLevelChange = (newLevel) => {
    setPriceLevel(newLevel);
    setHasUnsavedChanges(true);
    setLineItems(prev => prev.map(item => {
      if (item.isTextNote) return item;
      if (item.isNonStockCustom) return item;
      if (item.manualPrice) return item;
      if (item.itemPriceLevel) return item;
      // Through setLineBasePrice for the same reason as resetItemPrice above.
      if (item.productId) {
        const product = products.find(p => p.id === item.productId);
        if (product) return setLineBasePrice(item, getPriceForLevel(product, newLevel));
      }
      if (item.sku && item.sku !== '—' && item.sku !== 'NON-STOCK') {
        const product = products.find(p => p.sku === item.sku && p.isActive);
        if (product) return { ...setLineBasePrice(item, getPriceForLevel(product, newLevel)), productId: product.id };
      }
      return item;
    }));
  };

  // Track unsaved changes — only after initial load completes.
  // NOTE: `payments` is intentionally NOT watched here. The Payment section is
  // read-only in EditDocument (payments can only be recorded/deleted in
  // ViewDocument), so `payments` state only ever changes during load — the async
  // payments-collection fetch commits it after the mountedRef gate flips, which
  // used to falsely mark a freshly-opened, unedited document as dirty. Watching
  // it added no real-edit signal (users can't edit payments here), so it's excluded.
  // Capture the baseline once the document has finished loading. All 12 watched
  // fields are written before setLoadingDoc(false) in loadExistingDocument, so
  // when loadingDoc flips false the current state reflects the fully-loaded
  // document. Captured once; refreshed after a successful save/downgrade.
  useEffect(() => {
    if (loadingDoc || baselineRef.current !== null) return;
    baselineRef.current = JSON.stringify({
      selectedCustomer, lineItems, priceLevel, deliveryFee, discountPercent, discountEnabled, isDelivery,
      deliveryStreet, deliveryCity, deliveryState, deliveryZip, notes, poNumber, boardStatus,
    });
  }, [loadingDoc, selectedCustomer, lineItems, priceLevel, deliveryFee, discountPercent, discountEnabled, isDelivery,
      deliveryStreet, deliveryCity, deliveryState, deliveryZip, notes, poNumber, boardStatus]);

  // Track unsaved changes by comparing the current field values against the
  // baseline snapshot — immune to load timing. Load-time state population equals
  // the baseline (no false dirty); a real user edit differs and flags it.
  useEffect(() => {
    if (baselineRef.current === null) return; // not hydrated yet
    const snap = JSON.stringify({
      selectedCustomer, lineItems, priceLevel, deliveryFee, discountPercent, discountEnabled, isDelivery,
      deliveryStreet, deliveryCity, deliveryState, deliveryZip, notes, poNumber, boardStatus,
    });
    setHasUnsavedChanges(snap !== baselineRef.current);
  }, [selectedCustomer, lineItems, priceLevel, deliveryFee, discountPercent, discountEnabled, isDelivery,
      deliveryStreet, deliveryCity, deliveryState, deliveryZip, notes, poNumber, boardStatus]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && lineItems.length > 0) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, lineItems]);

  const confirmLeave = (destination) => {
    if (hasUnsavedChanges && lineItems.length > 0) {
      const confirmed = window.confirm(
        '⚠️ You have unsaved changes with items in this document. Your work will be lost if you leave without saving.\n\nDo you want to leave anyway?'
      );
      if (confirmed) {
        clearDraft(docType, id);
        navigate(destination);
      }
    } else {
      clearDraft(docType, id);
      navigate(destination);
    }
  };

  // All of these come back already rounded to cents from the shared helper, so
  // the figures shown here, the figures printed, and the figures written to
  // Firestore are the same numbers — and the printed line totals sum exactly to
  // the subtotal. Business rules (percentage discount off the subtotal, tax on
  // the POST-discount base, delivery never discounted and never taxed,
  // tax-exempt charged no tax) are unchanged; see computeDocTotals.
  const { subtotal, discountAmount, taxableBase, delivery, tax, grandTotal } =
    computeDocTotals(lineItems, {
      discountPercent,
      deliveryFee,
      taxExempt: docTaxExempt,
      taxRate: TAX_RATE,
    });

  // Reorder button state — based on the selected row's position in the array.
  const selectedLineIndex = lineItems.findIndex(it => it.id === selectedLineId);
  const canMoveLineUp = selectedLineIndex > 0;
  const canMoveLineDown = selectedLineIndex >= 0 && selectedLineIndex < lineItems.length - 1;

  // Phone matches on DIGITS ONLY, across EVERY number the customer has (phones
  // array + legacy field). Skipped when the term has no digits, so a name search
  // behaves exactly as it did before. `normalizePhone` is the same digits helper
  // the duplicate detection above uses.
  const customerSearchDigits = normalizePhone(customerSearch);
  // The customer's LIVE record, for the credit-hold warning. selectedCustomer
  // here is rebuilt from the SAVED DOCUMENT, which predates any hold and never
  // carries one, so the warning is read from the customers cache instead.
  const liveCustomer = customers.find(c => c.id === selectedCustomer?.id) || selectedCustomer;

  const filteredCustomers = customers.filter(c =>
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    (customerSearchDigits.length > 0 &&
      getAllPhoneNumbers(c).some(n => normalizePhone(n).includes(customerSearchDigits))) ||
    c.customerNumber?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  // Account-aware noun for THIS document's invoice-type labels (regular → sale).
  // Same complete derivation as ViewDocument: snapshot on the saved doc first, else
  // a live customer lookup BY customerId, else the loaded customer's flag, else
  // default regular. Memoized on [originalDoc, selectedCustomer, customers] so it
  // re-resolves once customers finish loading async (old docs lacking the snapshot
  // then resolve to Invoice via the customerId lookup instead of defaulting to Sale).
  const isAccount = useMemo(() => {
    if (originalDoc?.customerAccount != null) return originalDoc.customerAccount;
    const cid = originalDoc?.customerId;
    return (customers.find(c => c.id === cid)?.isAccountCustomer)
      ?? selectedCustomer?.isAccountCustomer
      ?? false;
  }, [originalDoc, selectedCustomer, customers]);
  const wordTitle = isAccount ? 'Invoice' : 'Sale';

  const filteredProducts = products.filter(p => {
    const matchSearch = !productSearch ||
      p.description?.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.sku?.toLowerCase().includes(productSearch.toLowerCase());
    const matchCategory = filterCategory === 'All' || p.category === filterCategory;
    return p.isActive && matchSearch && matchCategory;
  });

  const originalDate = originalDoc?.createdAt
    ? new Date(originalDoc.createdAt).toLocaleDateString('en-US')
    : '';

  // Payment status — from the shared engine, off the grand total on screen and
  // the invoice's payment records, so an edit that changes the total changes the
  // status as you type. Displayed only once the records are loaded.
  const payStatus = getPaymentStatus(grandTotal, payments);
  const { totalPaid, balanceDue, creditAmount } = payStatus;

  // Office Use: cost & margin computed values
  // Cost source priority: template's stored costPrice → non-stock per-item cost → product price-list costPrice → 0
  // The cost-source priority now lives in utils/lineMath.js (resolveUnitCost),
  // so this reducer, the other document editor and the line item editor cannot
  // disagree about what a line costs. Same priority, same lookup semantics —
  // the numbers are unchanged.
  const totalCost = lineItems.reduce(
    (sum, item) => sum + lineCost(item, resolveUnitCost(item, costLookup)), 0);
  // Profit is on the pre-tax sale of GOODS after discount — excludes delivery and tax.
  const grossProfit = taxableBase - totalCost;
  const profitMarginPct = taxableBase > 0 ? (grossProfit / taxableBase) * 100 : 0;

  // Inventory movement now flows through the shared ledger module (see the save
  // handler): getDeductableQuantities → computeInventoryDelta → applyInventoryDelta,
  // diffed against the invoice's persisted `deductedInventory` ledger. The old
  // stale-baseline delta + absolute-value stockQty write were removed here.

  // Re-open ONE estimate's estimator with ITS settings, as a modify of THAT
  // estimate. The draft records which one, so the return replaces its lines and
  // no one else's (see utils/estimates.js).
  const handleModifyEstimator = (estimate) => {
    if (!estimate) return;
    const confirmed = window.confirm(
      `Going back to the estimator will let you modify all settings for:\n\n${estimate.label}\n\nIf you click Calculate Materials, this estimate's items will be replaced with the new calculation. Other estimates and all other lines are not touched.\n\nIf you go back without recalculating, nothing changes.\n\nContinue?`
    );
    if (!confirmed) return;
    saveCurrentDraft({ pendingEstimate: pendingModify(estimate) });
    const estimatorRoute = estimate.kind === 'vinyl' ? '/vinyl-estimator'
      : estimate.kind === 'wood' ? '/wood-estimator'
      : estimate.kind === 'aluminum' ? '/aluminum-estimator'
      : '/estimator';
    navigate(estimatorRoute, {
      state: {
        returnTo: `/edit-document/${docType}/${id}`,
        previousSpecs: estimate.specs,
        priceLevel,
      },
    });
  };

  // Remove ONE estimate: its entry and every line carrying its id. The panel has
  // already confirmed, naming the estimate and its line count.
  const handleRemoveEstimate = (estimate) => {
    if (!estimate) return;
    const next = removeEstimate({ lineItems, estimates }, estimate.id);
    setLineItems(next.lineItems);
    setEstimates(next.estimates);
    setHasUnsavedChanges(true);
  };

  // ADD a chain link estimate. Before this the editor had no chain link entry at
  // all — chain link could only be reached by modifying an existing chain link
  // estimate, so a saved document could never gain its first one. Same shape as
  // the other three Add handlers here and as NewDocument's: the draft is saved
  // as an ADD, so the return appends a new estimate and replaces nothing.
  const handleAddFromEstimator = () => {
    saveCurrentDraft();
    navigate('/estimator', {
      state: {
        returnTo: `/edit-document/${docType}/${id}`,
        priceLevel,
      },
    });
  };

  const handleAddFromVinylEstimator = () => {
    saveCurrentDraft();
    navigate('/vinyl-estimator', {
      state: {
        returnTo: `/edit-document/${docType}/${id}`,
        priceLevel,
      },
    });
  };

  const handleAddFromWoodEstimator = () => {
    saveCurrentDraft();
    navigate('/wood-estimator', {
      state: {
        returnTo: `/edit-document/${docType}/${id}`,
        priceLevel,
      },
    });
  };

  const handleAddFromAluminumEstimator = () => {
    saveCurrentDraft();
    navigate('/aluminum-estimator', {
      state: {
        returnTo: `/edit-document/${docType}/${id}`,
        priceLevel,
      },
    });
  };

  // ── Downgrade invoice → quote ──
  // Reserve the next number from the shared, transaction-safe counters helper.
  const generateNextDocNumber = async (forType) => {
    return generateDocNumber(forType);
  };

  const handleDowngradeToQuote = async () => {
    // Downgrading DELETES this invoice, so "does it have payments?" is answered
    // from the payments collection read NOW — not from the page-open read,
    // which may have failed (leaving an empty list) or be out of date.
    let currentPayments;
    try {
      currentPayments = await fetchInvoicePayments(id);
      setPayments(currentPayments);
      setPaymentsLoaded(true);
      setPaymentsError(false);
    } catch (err) {
      console.error('Could not check payments before downgrade:', err);
      setPaymentsError(true);
      alert(
        `⛔ Cannot downgrade right now.\n\n` +
        `The payments recorded against this ${wordTitle.toLowerCase()} could not be checked, and one with payments must not be downgraded. ` +
        `Nothing was changed. Please try again.`
      );
      return;
    }
    // Block if any payments are recorded.
    const paid = sumPayments(currentPayments);
    const hasPayments = paid > 0 || currentPayments.length > 0 || (originalDoc?.payments?.length > 0);
    if (hasPayments) {
      alert(
        `❌ Cannot downgrade this ${wordTitle.toLowerCase()}.\n\n` +
        `This ${wordTitle.toLowerCase()} has $${paid.toFixed(2)} in recorded payments. ` +
        `Remove the payment(s) first if you really need to convert this back to a quote.`
      );
      return;
    }

    // Unsaved edits are discarded — the downgrade operates on the saved invoice.
    if (hasUnsavedChanges && lineItems.length > 0) {
      const confirmed = window.confirm(
        `You have unsaved changes that will be discarded. The downgrade uses the last saved version of this ${wordTitle.toLowerCase()}.\n\nContinue?`
      );
      if (!confirmed) return;
    }

    // Items that were tracked and would be returned to stock (from the saved invoice).
    const savedItems = originalDoc?.lineItems || lineItems;
    const inventoryItems = savedItems
      .map(item => {
        if (!item.productId || item.isNonStockCustom) return null;
        const product = products.find(p => p.id === item.productId);
        if (!product || !product.trackInventory) return null;
        const qty = parseFloat(item.qty) || 0;
        if (qty <= 0) return null;
        return { productId: item.productId, sku: item.sku, description: item.description, qty };
      })
      .filter(Boolean);

    setDowngradeInventoryItems(inventoryItems);
    setDowngradeStep('confirm');
    setShowDowngradeModal(true);
  };

  const performDowngrade = async (returnInventory) => {
    setDowngradeStep('processing');
    try {
      const newQuoteNumber = await generateNextDocNumber('quote');

      // Base the new quote on the saved invoice, then strip invoice-only fields.
      const { id: _omit, ...base } = originalDoc || {};
      const quoteData = {
        ...base,
        docNumber: newQuoteNumber,
        docType: 'quote',
        status: null,
        statusUpdatedAt: null,
        isPaid: false,
        isPartialPaid: false,
        totalPaid: 0,
        balanceDue: 0,
        paidAmount: 0,
        payments: [],
        inventoryDeducted: false,
        inventoryDeductedAt: null,
        // Clear the ledger so the downgraded quote carries no stale deductedInventory
        // (spread from ...base). Otherwise a later re-convert with skipped deduction
        // would read it as "already deducted" and under-deduct. Quotes never use the
        // ledger; the point is the NEXT invoice from re-convert starts clean.
        deductedInventory: null,
        downgradedFromInvoice: originalDoc?.docNumber || docNumber,
        downgradedAt: new Date().toISOString(),
        downgradedBy: userName,
        lastEditedBy: userName,
        lastEditedAt: new Date().toISOString(),
      };

      const newQuoteRef = await addDoc(collection(db, 'quotes'), quoteData);

      if (returnInventory && downgradeInventoryItems.length > 0) {
        const batch = writeBatch(db);
        const patches = [];
        downgradeInventoryItems.forEach(item => {
          const product = products.find(p => p.id === item.productId);
          if (!product) return;
          const newStock = (parseFloat(product.stockQty) || 0) + (parseFloat(item.qty) || 0);
          batch.update(doc(db, 'products', item.productId), { stockQty: newStock });
          patches.push([item.productId, newStock]);
        });
        await batch.commit();
        // Patch the cache in place (no full re-fetch) so returned stock stays correct.
        patches.forEach(([pid, s]) => updateProduct(pid, { stockQty: s }));
      }

      // Delete the original invoice.
      await deleteDoc(doc(db, 'invoices', id));

      setHasUnsavedChanges(false);
      // Refresh the baseline so the post-downgrade state is considered clean.
      baselineRef.current = JSON.stringify({
        selectedCustomer, lineItems, priceLevel, deliveryFee, isDelivery,
        deliveryStreet, deliveryCity, deliveryState, deliveryZip, notes, poNumber, boardStatus,
      });
      clearDraft(docType, id);
      setShowDowngradeModal(false);
      alert(`✅ ${wordTitle} downgraded to Quote #${newQuoteNumber}`);
      navigate(`/edit-document/quote/${newQuoteRef.id}`);
    } catch (err) {
      console.error('Error downgrading invoice:', err);
      alert('Error during downgrade: ' + err.message);
      setDowngradeStep('confirm');
    }
  };

  const handleSave = async () => {
    if (!selectedCustomer) { alert('⚠️ Please select a customer before saving.'); return false; }
    if (lineItems.length === 0) { alert('⚠️ Please add at least one item before saving.'); return false; }
    setSaving(true);
    let success = false;

    // ── THE PAYMENTS ARE READ HERE, ON EVERY SAVE, BEFORE ANYTHING IS WRITTEN ──
    //
    // This save used to write the payment fields only if the page-open read had
    // succeeded — and Save was live before that read finished, and a failed read
    // only went to the console. Either way the new grand total was saved while
    // the old paid status was silently left in place: paid in full, then +$200
    // delivery, still "PAID".
    //
    // The read is now part of the save. It runs even when the page-open read
    // succeeded, because that list is from when the page opened, and a payment
    // may have been taken at the counter since.
    //
    // If it fails, NOTHING is saved and the user is told plainly. An invoice is
    // never written with its payment state unknown.
    let savedPayments = null;
    if (docType === 'invoice') {
      try {
        savedPayments = await fetchInvoicePayments(id);
      } catch (err) {
        console.error('Save blocked — could not read payments:', err);
        setPaymentsError(true);
        setSaving(false);
        alert(
          `⛔ NOT SAVED.\n\n` +
          `The payments recorded against this ${wordTitle.toLowerCase()} could not be read, so whether it is paid, part-paid or owed cannot be worked out, ` +
          `and saving without that could record the wrong status.\n\n` +
          `Nothing was changed, and your edits are still on screen. Check the connection and press Save again.`
        );
        return false;
      }
      setPayments(savedPayments);
      setPaymentsLoaded(true);
      setPaymentsError(false);
    }

    try {
      const col = docType === 'quote' ? 'quotes' : 'invoices';
      const paymentFields = docType === 'invoice' ? {
        // Board status is independent of payments — always persist it for invoices.
        status: boardStatus,
        statusUpdatedAt: new Date().toISOString(),
        // A DERIVED MIRROR, never truth (see utils/paymentStatus.js): rebuilt from
        // the records just read and the grand total saved in this SAME write, so
        // the two cannot disagree. The embedded `payments` array is intentionally
        // NOT written (the collection is canonical).
        ...paymentMirrorFields(grandTotal, savedPayments),
      } : {};
      // Build the save payload as an object so the inventory ledger can be merged
      // in BEFORE the single Firestore write (keeping ledger ↔ document atomic).
      const docData = {
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.name,
        customerAddress: selectedCustomer.address,
        customerNumber: selectedCustomer.customerNumber,
        taxExempt: docTaxExempt,
        // Snapshot account status → invoice labels "INVOICE" (account) vs "SALE" (regular).
        customerAccount: !!selectedCustomer.isAccountCustomer,
        priceLevel,
        lineItems,
        subtotal,
        deliveryFee: delivery,
        tax,
        grandTotal,
        discountPercent: parseFloat(discountPercent) || 0,
        discountAmount,
        notes,
        poNumber: poNumber.trim(),
        isDelivery,
        deliveryStreet: isDelivery ? deliveryStreet : '',
        deliveryCity: isDelivery ? deliveryCity : '',
        deliveryState: isDelivery ? deliveryState : '',
        deliveryZip: isDelivery ? deliveryZip : '',
        // Every estimate; each line's estimateId travels inside lineItems.
        estimates,
        // Does this document carry estimator lines? Written the same way by both
        // pages. Only NewDocument used to write it, and there it meant "was the
        // page opened straight from an estimator" — nothing reads it, but a
        // stored flag should at least say something true.
        fromEstimator: lineItems.some(item => item && item.fromEstimator),
        // ⚠️ DERIVED MIRROR of the most recent estimate's settings, kept during
        // the transition for anything still reading the old single field.
        // `estimates` is the record — never read this back as truth.
        estimatorSpecs: estimatorSpecsMirror(estimates),
        lastEditedBy: userName,
        lastEditedAt: new Date().toISOString(),
        ...paymentFields,
      };

      // ── Ledger-based inventory sync (DEDUCTED invoices only) ──
      // Under the manual model, saving only auto-adjusts stock for an invoice that
      // is ALREADY 'deducted'; a 'pending' invoice never touches inventory on save
      // (the user deducts explicitly via Mark-as-Deducted). For a deducted invoice
      // we diff current deductable quantities against its persisted ledger
      // (getEffectiveLedger reads originalDoc.deductedInventory, or seeds "already
      // deducted" for legacy invoices with no ledger), apply ONLY the delta, then
      // stamp the new ledger onto docData so it persists in the SAME write below.
      // A no-change save yields an empty delta → no prompt, no writes.
      if (docType === 'invoice' && resolveInventoryStatus(originalDoc) === 'deducted') {
        const currentQuantities = getDeductableQuantities(lineItems, products);
        const ledger = getEffectiveLedger(originalDoc, originalLineItems, products);
        const changes = computeInventoryDelta(currentQuantities, ledger);

        if (changes.length > 0) {
          const rows = describeDelta(changes, products);
          const lines = rows.map(r =>
            `  ${r.direction === 'deduct' ? '−' : '+'}${r.qty}  ${r.sku} — ${r.description}`
          ).join('\n');
          const proceed = window.confirm(
            `Inventory changes to apply:\n\n${lines}\n\n(− = deduct from stock, + = return to stock)\n\nApply these changes?`
          );
          if (proceed) {
            try {
              await applyInventoryDelta({
                changes,
                products,
                docId: id,
                docType,
                docNumber,
                user,
                db,
              });
              // Server-side increment() leaves the DataContext cache stale — patch
              // each changed product optimistically (newStock = cached − delta; no
              // zero-clamp, matching the un-clamped server increment so real
              // discrepancies stay visible).
              changes.forEach(c => {
                const p = products.find(pp => pp.id === c.productId);
                if (p) updateProduct(c.productId, { stockQty: (parseFloat(p.stockQty) || 0) - c.delta });
              });
              // Record the new ledger ONLY after a successful apply, so a declined
              // or failed apply leaves the delta pending for the next save. Also
              // (re)stamp the explicit status so it stays authoritative 'deducted'
              // (never lost, and legacy invoices get an explicit field going forward).
              docData.deductedInventory = buildUpdatedLedger(currentQuantities);
              docData.inventoryStatus = 'deducted';
            } catch (invErr) {
              console.error('Inventory apply failed:', invErr);
              alert(`⚠️ Inventory was NOT applied.\n\n${invErr?.message || invErr}\n\nThe document will still be saved — correct stock manually in the Inventory page if needed.`);
              // Leave docData.deductedInventory unset so the ledger isn't advanced;
              // the pending delta is re-offered on the next save.
            }
          }
        }
      }

      await updateDoc(doc(db, col, id), docData);
      setHasUnsavedChanges(false);
      // Document is now clean from its newly-saved state — refresh the baseline.
      baselineRef.current = JSON.stringify({
        selectedCustomer, lineItems, priceLevel, deliveryFee, isDelivery,
        deliveryStreet, deliveryCity, deliveryState, deliveryZip, notes, poNumber, boardStatus,
      });
      setSaved(true);
      clearDraft(docType, id);
      // Keep the in-memory doc in sync with what we just wrote, so the ledger flow
      // (getEffectiveLedger → computeInventoryDelta) diffs against the CURRENT
      // ledger on the next save instead of the pre-save one. Without this, a
      // no-change save re-prompts for a delta that was already applied.
      setOriginalDoc(prev => ({ ...(prev || {}), ...docData }));
      setOriginalLineItems(lineItems);

      // Stay on the edit page after saving (edit is the default workspace now).
      // Briefly show the "Saved!" state, then re-enable editing.
      setTimeout(() => setSaved(false), 2000);
      success = true;
    } catch (err) {
      console.error('Error saving changes:', err);
      alert('Error saving. Please try again.');
    }
    setSaving(false);
    return success;
  };

  // Convert this quote to an invoice from the edit screen. Saves current edits
  // FIRST (so the invoice reflects what's on screen), re-reads the canonical saved
  // quote, then runs the SAME shared conversion the preview uses. Quote-only.
  const handleConvertToInvoice = async () => {
    // Block double-conversion: a quote can only be converted once. If the loaded
    // quote (originalDoc) already carries the marker, offer to open the existing
    // invoice instead of creating a duplicate.
    const alreadyNumber = originalDoc?.convertedToInvoice;
    if (alreadyNumber) {
      const existingId = originalDoc?.convertedToInvoiceId || null;
      const go = window.confirm(
        `⛔ This quote was already converted to invoice #${alreadyNumber}.\n\nA quote can only be converted once.\n\nOK = open invoice #${alreadyNumber}\nCancel = stay here`
      );
      if (go) {
        if (existingId) {
          navigate(`/view-document/invoice/${existingId}`);
        } else {
          // Legacy quote: no stored ID. Look up the invoice by number.
          try {
            const q = query(collection(db, 'invoices'), where('docNumber', '==', alreadyNumber), limit(1));
            const snap = await getDocs(q);
            if (!snap.empty) {
              navigate(`/view-document/invoice/${snap.docs[0].id}`);
            } else {
              alert(`Invoice #${alreadyNumber} could not be found. It may have been deleted.`);
            }
          } catch (e) {
            console.error('Lookup of converted invoice failed:', e);
            alert(`Could not open invoice #${alreadyNumber}.`);
          }
        }
      }
      return; // hard stop — never convert again
    }
    if (!window.confirm(`Convert Quote ${docNumber} to ${wordTitle}? This cannot be undone.`)) return;
    setConverting(true);
    try {
      const saved = await handleSave();
      if (!saved) { setConverting(false); return; } // save failed/validation — abort, nothing lost
      const snap = await getDoc(doc(db, 'quotes', id));
      if (!snap.exists()) throw new Error('Quote not found after save.');
      const quoteData = { id: snap.id, ...snap.data() };
      const { invoiceId, invoiceNumber } = await convertQuoteToInvoice({
        quoteId: id, quoteData, isAccount, products, updateProduct, user,
      });
      alert(`Quote converted to ${wordTitle} ${invoiceNumber} successfully!`);
      navigate(`/edit-document/invoice/${invoiceId}`);
    } catch (err) {
      console.error('Conversion error:', err);
      alert('Error converting. Please try again.');
    }
    setConverting(false);
  };

  // ── Round Up Quantities ──
  // Which products round is the OWNER'S choice, made per product in Inventory
  // with the "Round up to full package on quotes" tick, and the size comes from
  // that product's own packQty. There is no table here and none in the other
  // editor either.
  //
  // THE ALERT SAYS THREE DIFFERENT THINGS, and staying quiet is the fourth:
  //   rounded        — what moved, and from what to what
  //   marked, unset  — the owner asked for rounding on a product whose record
  //                    cannot say what a package holds. Named out loud: it is a
  //                    half-finished record, and only the owner can finish it.
  //   skipped        — the line matches no product at all (template lines carry
  //                    sku 'TEMPLATE' and no productId). These were previously
  //                    neither rounded NOR reported, which is the exact silent
  //                    no-op that let barbed wire go unrounded for a year.
  //   silence        — a product with a pack size that is NOT marked to round.
  //                    That is nearly every product in the catalogue; saying so
  //                    every time would bury the two messages above.
  const roundUpQuantities = () => {
    const { items, changes, missing, skipped } = roundLineItems(lineItems, products);
    setLineItems(items);

    const lines = [];
    if (changes.length > 0) {
      lines.push(`✅ Rounded up ${changes.length} item(s):`);
      lines.push('');
      changes.forEach(c => lines.push(`${c.description}: ${c.from} → ${c.to}`));
    }
    if (missing.length > 0) {
      if (lines.length > 0) lines.push('');
      lines.push('⚠️ Marked to round up, but no pack size is set:');
      missing.forEach(m => lines.push(
        `${m.sku} — ${m.description}. Set pcs per package in Inventory.`
      ));
    }
    if (skipped.length > 0) {
      if (lines.length > 0) lines.push('');
      lines.push('ℹ️ Skipped — these lines are not linked to a product, so there is');
      lines.push('nothing to look a pack size up on:');
      skipped.forEach(s => lines.push(`${s.sku} — ${s.description}`));
    }
    alert(lines.length > 0 ? lines.join('\n') : 'No items needed rounding up.');
  };

  if (loadingDoc) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#CDE7F5' }}>
        <p className="text-gray-500 text-xl">Loading document...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#CDE7F5', padding: 20 }}>

      {/* Office Use Modal — screen only, never prints */}
      {showOfficeUse && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 print:hidden">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800 text-base">🔒 Office Use Only — Internal</h3>
              <button onClick={() => setShowOfficeUse(false)} className="text-gray-400 hover:text-gray-600 font-bold text-xl leading-none">✕</button>
            </div>

            <div className="space-y-1 text-sm">
              <div className="flex justify-between py-1">
                <span className="text-gray-600">Sale Total (pre-tax)</span>
                <span className="font-bold">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-600">Sales Tax (6%) <span className="text-[10px] text-gray-400">(excluded from profit)</span></span>
                <span className="font-bold">${tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-600">Delivery Fee <span className="text-[10px] text-gray-400">(excluded from profit)</span></span>
                <span className="font-bold">{isDelivery ? `$${delivery.toFixed(2)}` : 'N/A'}</span>
              </div>
              <div className="flex justify-between py-2 border-t border-b border-gray-200 my-1">
                <span className="font-bold text-gray-800">Grand Total</span>
                <span className="font-bold text-blue-700">${grandTotal.toFixed(2)}</span>
              </div>

              <div className="flex justify-between py-1 pt-2">
                <span className="text-gray-500">Total Cost of Materials</span>
                <span className="font-bold text-gray-500">${totalCost.toFixed(2)}</span>
              </div>

              <div className="border-t border-gray-100 pt-2 mt-1 space-y-1">
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Gross Profit</span>
                  <span className={`font-bold ${grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ${grossProfit.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-gray-600">Profit Margin</span>
                  <span className={`font-bold text-base ${
                    profitMarginPct >= 20 ? 'text-green-600' :
                    profitMarginPct >= 10 ? 'text-yellow-500' :
                    'text-red-600'
                  }`}>
                    {profitMarginPct.toFixed(1)}%
                  </span>
                </div>
                <p className="text-[10px] text-gray-400 mt-1 mb-0">
                  Gross Profit &amp; Margin are based on the discounted pre-tax product sale — excluding tax and delivery.
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowOfficeUse(false)}
              className="w-full mt-5 bg-gray-800 hover:bg-gray-900 text-white font-bold py-2.5 rounded-xl transition text-sm"
            >
              Close
            </button>
            <p className="text-center text-xs text-gray-400 mt-3">⚠️ Internal use only — do not share with customer</p>
          </div>
        </div>
      )}

      <div style={{ maxWidth: 1280, margin: '0 auto' }}>

        {/* Header card — flat NewDocument-style (replaces the old blue top bar) */}
        <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 12, flexShrink: 0 }}>
              <img src={require('../assets/fenceflow-logo.png')} alt="Fence Flow" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8 }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#03045E' }}>
                  {docType === 'quote' ? '📄 Sale Quote' : `🧾 ${wordTitle}`}
                </h1>
                <span style={{ fontFamily: 'monospace', color: '#0077B6', fontSize: 16, fontWeight: 700, background: '#E6F1FB', padding: '4px 12px', borderRadius: 8, border: '1px solid #E3ECF5' }}>
                  #{docNumber}
                </span>
                {docType === 'invoice' && (
                  !paymentsLoaded ? (
                    <span style={{ background: '#FDF1DD', color: '#854F0B', border: '1px solid #C99A33', padding: '3px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>
                      {paymentsError ? '⚠️ PAYMENTS NOT LOADED' : '… CHECKING PAYMENTS'}
                    </span>
                  ) : payStatus.status === PAYMENT_STATUS.CREDIT ? (
                    <span style={{ background: '#E6F1FB', color: '#0077B6', border: '1px solid #0077B6', padding: '3px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>💙 CREDIT — ${creditAmount.toFixed(2)} overpaid</span>
                  ) : payStatus.status === PAYMENT_STATUS.PAID ? (
                    <span style={{ background: '#E6F7EE', color: '#0F6E56', border: '1px solid #0F6E56', padding: '3px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>✅ PAID IN FULL</span>
                  ) : payStatus.status === PAYMENT_STATUS.PARTIAL ? (
                    <span style={{ background: '#FFF7E6', color: '#9A6B00', border: '1px solid #C99A33', padding: '3px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>💛 PARTIAL — ${balanceDue.toFixed(2)} left</span>
                  ) : (
                    <span style={{ background: '#FBECEC', color: '#A32D2D', border: '1px solid #A32D2D', padding: '3px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700 }}>🔴 UNPAID</span>
                  )
                )}
              </div>
              <p style={{ color: '#6B7C93', fontSize: 12, margin: '4px 0 0 0', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600 }}>
                Salesperson: <span style={{ color: '#0077B6' }}>{originalDoc?.salesperson || userName}</span> · {originalDate}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button onClick={handleSave} disabled={saving || saved || !selectedCustomer || lineItems.length === 0}
              style={{
                background: '#0077B6', border: 'none', color: '#fff', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 700, transition: 'background 0.3s',
                opacity: (saving || saved || !selectedCustomer || lineItems.length === 0) ? 0.6 : 1,
                cursor: (saving || saved || !selectedCustomer || lineItems.length === 0) ? 'not-allowed' : 'pointer',
              }}>
              {saving ? 'Saving...' : saved ? '✅ Saved!' : '💾 Save Changes'}
            </button>
            {docType === 'invoice' && (
              <select
                value={boardStatus}
                onChange={e => setBoardStatus(e.target.value)}
                title="Board status"
                style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E', padding: '10px 12px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', outline: 'none', fontFamily: 'inherit' }}
                onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
              >
                {BOARD_STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            )}
            <button
              onClick={() => setShowOfficeUse(true)}
              title="Office Use Only"
              style={{ background: '#E6F1FB', border: '1px solid #0077B6', color: '#0077B6', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s' }}
            >
              🔒 OU
            </button>
            <button
              onClick={() => {
                if (hasUnsavedChanges && lineItems.length > 0) {
                  const confirmed = window.confirm(
                    'You have unsaved changes. Save first so the preview shows the latest version.\n\nContinue to preview without saving?'
                  );
                  if (!confirmed) return;
                }
                navigate(`/view-document/${docType}/${id}`);
              }}
              title="Preview before printing"
              style={{ background: '#E6F1FB', border: '1px solid #0077B6', color: '#0077B6', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s' }}
            >
              👁️ Preview
            </button>
            <button
              onClick={() => {
                if (hasUnsavedChanges && lineItems.length > 0) {
                  const confirmed = window.confirm(
                    'You have unsaved changes. Save first so the preview shows the latest version.\n\nContinue to preview without saving?'
                  );
                  if (!confirmed) return;
                }
                navigate(`/view-document/${docType}/${id}`, { state: { autoPrint: true } });
              }}
              title="Print or save as PDF"
              style={{ background: '#E0F7FA', border: '1px solid #0096C7', color: '#0096C7', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s' }}
            >
              🖨️ Print / PDF
            </button>
            <button
              onClick={() => confirmLeave('/dashboard')}
              style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s' }}
            >
              ← Dashboard
            </button>
          </div>
        </div>

        {/* Edit warning banner — flat soft-amber caution cue */}
        <div style={{ background: '#FAEEDA', border: '1px solid #EBD9B4', borderRadius: 16, padding: '14px 18px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 22, lineHeight: 1 }}>⚠️</span>
          <div>
            <p style={{ color: '#854F0B', fontWeight: 700, margin: 0, fontSize: 14 }}>You are editing an existing {docType === 'quote' ? 'quote' : wordTitle.toLowerCase()}.</p>
            <p style={{ color: '#9A6B2E', fontSize: 13, margin: '2px 0 0 0' }}>Changes will be saved to the original document #{docNumber}.</p>
          </div>
        </div>

        {saved && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-6 py-4 rounded-xl mb-6 font-bold text-center">
            ✅ Changes saved! Redirecting...
          </div>
        )}

        {/* Customer — flat NewDocument-style card */}
        <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16, position: 'relative', zIndex: 30, overflow: 'visible' }}>
          <label style={{ color: '#6B7C93', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>Customer <span style={{ color: '#E24B4A' }}>*</span></label>
          {/* The hold, for as long as the document is being edited. Read from
              the CUSTOMER record (live status), not from the document — which
              was saved before the hold existed. Warns only; nothing here stops
              the document being edited or saved. */}
          <CreditHoldWarning customer={liveCustomer} style={{ marginBottom: 12 }} />
          {selectedCustomer ? (
            <div style={{ background: '#E6F1FB', border: '1px solid #E3ECF5', borderRadius: 12, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <p style={{ color: '#0077B6', fontWeight: 700, fontSize: 16, margin: 0 }}>{selectedCustomer.name}</p>
                <p style={{ color: '#6B7C93', fontSize: 13, margin: '4px 0 0 0' }}>{selectedCustomer.address}</p>
                <p style={{ color: '#6B7C93', fontSize: 13, margin: '2px 0 0 0' }}>
                  <span style={{ color: '#6B7C93', fontFamily: 'monospace', fontWeight: 700 }}>{selectedCustomer.customerNumber}</span>
                  {selectedCustomer.taxExempt && (
                    <span style={{ background: '#E6F7EE', color: '#0F6E56', padding: '2px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, marginLeft: 10, letterSpacing: 0.5 }}>TAX EXEMPT</span>
                  )}
                  {isDelivery && (
                    <span style={{ background: '#E6F1FB', color: '#0077B6', padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700, marginLeft: 8 }}>🚚 DELIVERY ORDER</span>
                  )}
                </p>
              </div>
              <button onClick={() => setSelectedCustomer(null)} style={{ background: '#FBECEC', border: '1px solid #E9C3C3', color: '#A32D2D', padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s' }}>✕ Change</button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, position: 'relative' }}>
              <input
                type="text"
                placeholder="Search customer by name, phone, or number..."
                value={customerSearch}
                onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                onFocus={(e) => { setShowCustomerDropdown(true); e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
              />
              {showCustomerDropdown && customerSearch && (
                <div style={{
                  position: 'absolute', zIndex: 100, top: 'calc(100% + 6px)', left: 0, right: 0,
                  background: '#FFFFFF',
                  border: '1px solid #E3ECF5', borderRadius: 12,
                  maxHeight: 240, overflowY: 'auto',
                  boxShadow: '0 12px 30px rgba(3, 4, 94, 0.12)',
                }}>
                  {filteredCustomers.length === 0 ? (
                    <div style={{ padding: '12px 16px' }}>
                      <p style={{ color: '#6B7C93', fontSize: 13, margin: 0 }}>No customers found.</p>
                      <button onClick={openNewCustomerModal} style={{ background: 'transparent', border: 'none', color: '#0077B6', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '4px 0 0 0' }}>+ Create New Customer</button>
                    </div>
                  ) : (
                    filteredCustomers.map(c => (
                      <div key={c.id}
                        onClick={() => { setSelectedCustomer(c); setShowCustomerDropdown(false); setCustomerSearch(''); }}
                        style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #E3ECF5', transition: 'background 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#E6F1FB'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <p style={{ color: '#03045E', fontWeight: 600, fontSize: 14, margin: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{c.name}<CreditHoldWarning customer={c} size="compact" /></p>
                        <p style={{ color: '#6B7C93', fontSize: 12, margin: '2px 0 0 0' }}>
                          <span style={{ color: '#0077B6', fontFamily: 'monospace' }}>{c.customerNumber}</span> · {c.phone}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}
              </div>
              <button onClick={openNewCustomerModal}
                style={{ background: '#E6F1FB', border: '1px solid #0077B6', color: '#0077B6', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.3s', whiteSpace: 'nowrap', flexShrink: 0 }}>
                + New Customer
              </button>
            </div>
          )}
        </div>

        {/* P.O. Number + Price Level — flat NewDocument-style grid cards
            (board status lives in the top header card; not duplicated here) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20 }}>
            <label style={{ color: '#6B7C93', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>P.O. Number</label>
            <input
              type="text"
              value={poNumber}
              onChange={e => setPoNumber(e.target.value)}
              placeholder="Customer P.O. number (optional)"
              style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
              onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
              onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
            />
          </div>
          <div style={{ background: '#F3F2FD', border: '2px solid #534AB7', borderRadius: 16, padding: 20 }}>
            <label style={{ color: '#3C3489', fontSize: 11, fontWeight: 500, letterSpacing: '0.03em', textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>PRICE LEVEL</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {['A', 'B', 'C'].map(level => {
                const isActive = priceLevel === level;
                return (
                  <button key={level} onClick={() => handlePriceLevelChange(level)}
                    style={{
                      flex: 1, padding: 10,
                      background: isActive ? '#534AB7' : '#FFFFFF',
                      border: `1px solid ${isActive ? '#534AB7' : '#CECBF6'}`,
                      borderRadius: 10,
                      color: isActive ? '#FFFFFF' : '#534AB7',
                      fontSize: 13, fontWeight: 700, cursor: 'pointer',
                      transition: 'background 0.3s, border-color 0.3s, color 0.3s',
                    }}>
                    {level}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Line Items — flat NewDocument-style card */}
        <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16, position: 'relative', zIndex: 20, overflow: 'visible' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <h3 style={{ color: '#03045E', fontWeight: 700, fontSize: 16, margin: 0, letterSpacing: 0.5 }}>
                📋 Line Items
                {lineItems.filter(i => i.fromEstimator).length > 0 && (
                  <span style={{ marginLeft: 10, background: '#E6F1FB', color: '#0077B6', padding: '2px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>
                    🔧 {lineItems.filter(i => i.fromEstimator).length} from Estimator
                  </span>
                )}
              </h3>
              {lineItems.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} title={selectedLineId ? '' : 'Select a line item first'}>
                  <button type="button" onClick={() => moveLineItem('up')} disabled={!canMoveLineUp} title="Move up"
                    style={{ background: canMoveLineUp ? '#E6F1FB' : '#CDE7F5', border: '1px solid #E3ECF5', color: canMoveLineUp ? '#0077B6' : '#C3CBD6', width: 32, height: 32, borderRadius: 8, fontSize: 13, fontWeight: 700, lineHeight: 1, cursor: canMoveLineUp ? 'pointer' : 'default' }}>▲</button>
                  <button type="button" onClick={() => moveLineItem('down')} disabled={!canMoveLineDown} title="Move down"
                    style={{ background: canMoveLineDown ? '#E6F1FB' : '#CDE7F5', border: '1px solid #E3ECF5', color: canMoveLineDown ? '#0077B6' : '#C3CBD6', width: 32, height: 32, borderRadius: 8, fontSize: 13, fontWeight: 700, lineHeight: 1, cursor: canMoveLineDown ? 'pointer' : 'default' }}>▼</button>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {/* All four estimators, exactly as NewDocument offers them. Every
                  item here is an ADD: a new estimate is appended and nothing
                  existing is replaced. Changing an existing estimate is the
                  Modify control beside it. */}
              <EstimatorMenu
                onChainLink={handleAddFromEstimator}
                onVinyl={handleAddFromVinylEstimator}
                onWood={handleAddFromWoodEstimator}
                onAluminum={handleAddFromAluminumEstimator}
              />
              {/* One estimate: a single "Modify <label>" button here, where the
                  old Modify button was. Two or more: EstimatesList below. */}
              <EstimateModifyControl
                estimates={estimates}
                lineItems={lineItems}
                onModify={handleModifyEstimator}
                onRemove={handleRemoveEstimate}
              />
              <button
                type="button"
                onClick={() => setShowTemplatesPicker(true)}
                style={{ background: '#E4F3FA', border: '1px solid #0096C7', color: '#0096C7', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                📋 Templates
              </button>
              <button
                type="button"
                onClick={() => { setShowNonStockForm(true); setShowProductDropdown(false); }}
                style={{ background: '#E0F7FA', border: '1px solid #00B4D8', color: '#0096C7', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                + Non-Stock
              </button>
              <button
                type="button"
                onClick={() => { setLineItems(prev => [...prev, { id: Date.now(), isTextNote: true, description: '' }]); setHasUnsavedChanges(true); }}
                style={{ background: '#EEF2F7', border: '1px solid #9AA8BC', color: '#4A5A70', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                + Text/Note
              </button>
              <button
                type="button"
                onClick={roundUpQuantities}
                title="Rounds each line up to a whole package, using the pack size on the product"
                style={{ background: '#EAF8FC', border: '1px solid #48CAE4', color: '#0096C7', padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(3,4,94,0.12)', transition: 'box-shadow 0.15s, transform 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 10px rgba(3,4,94,0.18)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(3,4,94,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                🔄 Round Up
              </button>
            </div>
          </div>

          {/* Two or more estimates: one row each. Also the note about estimator
              lines from older documents that belong to no estimate. */}
          <EstimatesList
            estimates={estimates}
            lineItems={lineItems}
            onModify={handleModifyEstimator}
            onRemove={handleRemoveEstimate}
          />

          <div className="product-search-container" style={{ position: 'relative' }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setShowProductPicker(true)}
                style={{ background: '#0077B6', border: 'none', color: '#fff', padding: '10px 18px', borderRadius: 12, fontSize: 13, fontWeight: 700, letterSpacing: 0.5, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,119,182,0.3)', transition: 'transform 0.3s, box-shadow 0.3s', flex: '0 0 auto' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,119,182,0.45)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,119,182,0.3)'; }}
              >
                🛒 Add Products
              </button>
              <div style={{ flex: 1, position: 'relative', minWidth: 260 }}>
                <input
                  type="text"
                  placeholder="Quick search by description or SKU..."
                  value={productSearch}
                  onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                  onFocus={(e) => { setShowProductDropdown(true); e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                  style={{ width: '100%', padding: '10px 14px', paddingLeft: 44, background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }}
                />
                <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#6B7C93', fontSize: 16, pointerEvents: 'none' }}>🔍</span>
              </div>
            </div>

            {/* Product Dropdown */}
            {showProductDropdown && (productSearch || filterCategory !== 'All') && (
              <div style={{
                position: 'absolute', zIndex: 100, top: 'calc(100% - 4px)', left: 0, right: 0,
                background: '#FFFFFF',
                border: '1px solid #E3ECF5', borderRadius: 12,
                maxHeight: 280, overflowY: 'auto',
                boxShadow: '0 12px 30px rgba(3, 4, 94, 0.12)',
              }}>
                {filteredProducts.length === 0 ? (
                  <p style={{ padding: '12px 16px', color: '#6B7C93', margin: 0 }}>No products found.</p>
                ) : (
                  filteredProducts.slice(0, 50).map(p => (
                    <div key={p.id} onMouseDown={() => addLineItem(p)}
                      style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #E3ECF5', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'background 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#E6F1FB'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ color: '#03045E', fontWeight: 600, fontSize: 13, margin: 0 }}>{p.description}{stockBadge(p)}</p>
                        <p style={{ color: '#6B7C93', fontSize: 11, margin: '2px 0 0 0' }}>
                          <span style={{ color: '#0077B6', fontFamily: 'monospace', fontWeight: 700 }}>SKU: {p.sku}</span>
                          {' · '}{p.unit} · {p.category}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right', marginLeft: 16, flexShrink: 0 }}>
                        <p style={{ color: priceLevel === 'A' ? '#0077B6' : '#6B7C93', fontSize: 11, fontWeight: priceLevel === 'A' ? 700 : 500, margin: 0 }}>A: ${parseFloat(p.priceA || 0).toFixed(2)}</p>
                        <p style={{ color: priceLevel === 'B' ? '#0077B6' : '#6B7C93', fontSize: 11, fontWeight: priceLevel === 'B' ? 700 : 500, margin: 0 }}>B: ${parseFloat(p.priceB || 0).toFixed(2)}</p>
                        <p style={{ color: priceLevel === 'C' ? '#0077B6' : '#6B7C93', fontSize: 11, fontWeight: priceLevel === 'C' ? 700 : 500, margin: 0 }}>C: ${parseFloat(p.priceC || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Items Table */}
          {lineItems.length === 0 && !showNonStockForm ? (
            <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl">
              <p className="text-lg">No items added yet.</p>
              <p className="text-sm">Select a category, search for a product, or add a non-stock item above.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#E6F1FB' }}>
                  <th className="px-3 py-2 text-left" style={{ color: '#6B7C93' }}>SKU</th>
                  <th className="px-3 py-2 text-left" style={{ color: '#6B7C93' }}>Description</th>
                  <th className="px-3 py-2 text-center" style={{ color: '#6B7C93' }}>Unit</th>
                  <th className="px-3 py-2 text-center" style={{ color: '#6B7C93' }}>Qty</th>
                  <th className="px-3 py-2 text-right" style={{ color: '#6B7C93' }}>Unit Price</th>
                  <th className="px-3 py-2 text-right" style={{ color: '#6B7C93' }}>Total</th>
                  <th className="px-3 py-2 text-center" style={{ color: '#6B7C93' }}>Remove</th>
                </tr>
              </thead>
              <tbody>
                {/* Inline non-stock entry row */}
                {showNonStockForm && (
                  <tr style={{ background: '#CDE7F5', borderBottom: '2px solid #E3ECF5' }}>
                    <td className="px-3 py-2">
                      <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded text-xs font-bold">NON-STOCK</span>
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        placeholder="Enter item description..."
                        value={nonStockForm.description}
                        onChange={(e) => setNonStockForm(f => ({ ...f, description: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && confirmNonStockItem()}
                        onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                        onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                        style={{ width: '100%', border: '1px solid #E3ECF5', borderRadius: 8, padding: '6px 8px', fontSize: 13, color: '#03045E', background: '#FFFFFF', outline: 'none', boxSizing: 'border-box' }}
                        autoFocus
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <select value={nonStockForm.unit} onChange={(e) => setNonStockForm(f => ({ ...f, unit: e.target.value }))}
                        style={{ border: '1px solid #E3ECF5', borderRadius: 8, padding: '4px 6px', fontSize: 12, background: '#FFFFFF', color: '#03045E' }}>
                        {['pc','ft','roll','bag','lb','ea','lot'].map(u => <option key={u}>{u}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input type="number" value={nonStockForm.qty}
                        onChange={(e) => setNonStockForm(f => ({ ...f, qty: e.target.value }))}
                        onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                        onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                        style={{ width: 64, border: '1px solid #E3ECF5', borderRadius: 8, padding: '6px 8px', textAlign: 'center', fontSize: 13, color: '#03045E', background: '#FFFFFF', outline: 'none', boxSizing: 'border-box' }} min="0.01" step="0.01" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <input type="number" value={nonStockForm.cost}
                          onChange={(e) => setNonStockForm(f => ({ ...f, cost: e.target.value }))}
                          onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                          onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                          title="COST (per unit)"
                          style={{ width: 96, border: '1px solid #E3ECF5', borderRadius: 8, padding: '6px 8px', textAlign: 'right', fontSize: 13, color: '#03045E', background: '#FFFFFF', outline: 'none', boxSizing: 'border-box' }} step="0.01" placeholder="COST" />
                        <input type="number" value={nonStockForm.unitPrice}
                          onChange={(e) => setNonStockForm(f => ({ ...f, unitPrice: e.target.value }))}
                          onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                          onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                          style={{ width: 96, border: '1px solid #E3ECF5', borderRadius: 8, padding: '6px 8px', textAlign: 'right', fontSize: 13, color: '#03045E', background: '#FFFFFF', outline: 'none', boxSizing: 'border-box' }} step="0.01" placeholder="0.00" />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-sm" style={{ color: '#0077B6' }}>
                      ${round2((parseFloat(nonStockForm.qty)||0)*(parseFloat(nonStockForm.unitPrice)||0)).toFixed(2)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <button onClick={confirmNonStockItem}
                          style={{ background: '#0077B6', border: 'none', color: '#fff', fontWeight: 700, padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>Add</button>
                        <button onClick={() => { setShowNonStockForm(false); setNonStockForm({ description: '', qty: 1, unit: 'pc', unitPrice: '', cost: '' }); }}
                          style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#6B7C93', fontWeight: 600, padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                )}
                {(() => {
                  const rows = [];
                  let lastGroupId = null;
                  lineItems.forEach((item, index) => {
                    // Text/Note — SAME column structure as a normal row: text input
                    // in the Description cell only, other cells blank, ✕ remove in its
                    // usual last cell. The blank cells give a clickable area to SELECT
                    // the row so the ▲▼ reorder toolbar can move it.
                    if (item.isTextNote) {
                      rows.push(
                        <tr key={item.id}
                          onClick={(e) => {
                            if (e.target.closest('input, button, select, textarea, label')) return;
                            setSelectedLineId(item.id);
                          }}
                          style={{ background: selectedLineId === item.id ? '#E6F1FB' : (index % 2 === 0 ? '#FFFFFF' : '#F8FBFE'), boxShadow: selectedLineId === item.id ? 'inset 3px 0 0 #0077B6' : undefined, borderBottom: '1px solid #E3ECF5', cursor: 'pointer' }}>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2">
                            <input type="text" value={item.description}
                              onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                              placeholder="Note / heading…"
                              onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                              onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                              style={{ width: '100%', minWidth: 0, border: '1px solid #E3ECF5', borderRadius: 8, padding: '6px 8px', fontSize: 13, color: '#03045E', background: '#FFFFFF', outline: 'none' }} />
                          </td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2 text-center">
                            <button onClick={() => removeLineItem(item.id)} style={{ background: 'transparent', border: 'none', color: '#A32D2D', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>✕</button>
                          </td>
                        </tr>
                      );
                      return;
                    }
                    const groupId = item.templateBundleId || item.stockBundleId || null;
                    const groupName = item.templateBundleName || item.stockBundleName;
                    const isStockBundle = !!item.stockBundleId;
                    if (groupId && groupId !== lastGroupId) {
                      rows.push(
                        <tr key={`bundle-${groupId}-${index}`} style={{ background: '#E6F1FB' }}>
                          <td colSpan={7} className="px-3 py-2">
                            <p className="font-bold text-sm flex items-center gap-2" style={{ color: '#0077B6' }}>
                              {isStockBundle ? '📦' : '📋'} {groupName}
                            </p>
                          </td>
                        </tr>
                      );
                      lastGroupId = groupId;
                    } else if (!groupId) {
                      lastGroupId = null;
                    }
                    const bundleStyle = groupId
                      ? { borderLeft: `3px solid ${isStockBundle ? '#0077B6' : '#00B4D8'}` }
                      : {};
                    rows.push(
                      <tr key={item.id}
                        onClick={(e) => {
                          // Clicks on inline controls (inputs, checkbox+label, remove) must
                          // not select the row — let them behave normally.
                          if (e.target.closest('input, button, select, textarea, label')) return;
                          setSelectedLineId(item.id);
                        }}
                        onDoubleClick={(e) => {
                          if (e.target.closest('input, button, select, textarea, label')) return;
                          openItemEditor(item);
                        }}
                        style={{
                          ...bundleStyle,
                          cursor: 'pointer',
                          borderBottom: '1px solid #E3ECF5',
                          boxShadow: selectedLineId === item.id ? 'inset 3px 0 0 #0077B6' : undefined,
                          background: selectedLineId === item.id ? '#E6F1FB'
                            : item.fromEstimator ? '#EFF6FC'
                            : item.fromStockBundle ? '#EAF4FB'
                            : item.fromTemplate ? '#EEF1FB'
                            : item.isNonStockCustom ? '#FBF5EC'
                            : '#FFFFFF',
                        }}>
                        <td className="px-3 py-2 font-mono text-xs font-bold" style={{ color: '#0077B6' }}>{item.sku}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            {item.isNonStockCustom && (
                              <span className="bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded text-xs font-bold flex-shrink-0">NON-STOCK</span>
                            )}
                            {item.fromTemplate && (
                              <span className="bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded text-xs font-bold flex-shrink-0" title={item.templateBundleName || item.templateName}>📋 TEMPLATE</span>
                            )}
                            {item.fromStockBundle && (
                              <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs font-bold flex-shrink-0" title={item.stockBundleName}>📦 BUNDLE</span>
                            )}
                            {item.fromEstimator && !item.fromTemplate && !item.fromStockBundle && (
                              <span title="From Estimator" className="text-blue-500 text-xs flex-shrink-0">🔧</span>
                            )}
                            {item.itemPriceLevel && (
                              <span title={`Per-item Level ${item.itemPriceLevel}`}
                                className={`px-1.5 py-0.5 rounded text-xs font-bold flex-shrink-0 ${
                                  item.itemPriceLevel === 'A' ? 'bg-blue-100 text-blue-700' :
                                  item.itemPriceLevel === 'B' ? 'bg-green-100 text-green-700' :
                                  'bg-amber-100 text-amber-700'
                                }`}>
                                [{item.itemPriceLevel}]
                              </span>
                            )}
                            {item.isReturnCredit && (
                              <span className="flex-shrink-0 px-1.5 py-0.5 rounded text-xs font-bold" style={{ background: '#FBECEC', color: '#A32D2D' }}>↩ RETURN</span>
                            )}
                            <input type="text" value={item.description}
                              onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                              onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                              onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                              style={{ flex: 1, minWidth: 0, border: '1px solid #E3ECF5', borderRadius: 8, padding: '6px 8px', fontSize: 13, color: '#03045E', background: '#FFFFFF', outline: 'none' }} />
                          </div>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: item.isReturnCredit ? '#A32D2D' : '#6B7C93' }}>
                            <input type="checkbox" checked={!!item.isReturnCredit}
                              onChange={(e) => updateLineItem(item.id, 'isReturnCredit', e.target.checked)}
                              style={{ accentColor: '#A32D2D', width: 14, height: 14 }} />
                            Return Credit to Customer
                          </label>
                          {/* ON PLATE — on EVERY product line, not just the ones
                              that look like posts. The user ticks it where it
                              belongs; nothing here guesses. */}
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, marginLeft: 14, cursor: 'pointer', fontSize: 11, fontWeight: 600, color: isOnPlate(item) ? '#0F6E56' : '#6B7C93' }}>
                            <input type="checkbox" checked={isOnPlate(item)}
                              onChange={(e) => toggleOnPlate(item.id, e.target.checked)}
                              style={{ accentColor: '#0F6E56', width: 14, height: 14 }} />
                            On Plate (+${ON_PLATE_SURCHARGE} each)
                          </label>
                        </td>
                        <td className="px-3 py-2 text-center" style={{ color: '#6B7C93' }}>{item.unit}</td>
                        <td className="px-3 py-2 text-center">
                          <input type="number" value={item.qty}
                            onChange={(e) => updateLineItem(item.id, 'qty', e.target.value)}
                            onFocus={(e) => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                            onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                            style={{ width: 64, border: '1px solid #E3ECF5', borderRadius: 8, padding: '6px 8px', textAlign: 'center', fontSize: 13, color: '#03045E', background: '#FFFFFF', outline: 'none', boxSizing: 'border-box' }} min="1" />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <input type="number" value={item.unitPrice}
                              onChange={(e) => updateLineItem(item.id, 'unitPrice', e.target.value)}
                              onFocus={(e) => { e.target.select(); e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                              onBlur={(e) => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                              style={{ width: 96, border: '1px solid #E3ECF5', borderRadius: 8, padding: '6px 8px', textAlign: 'right', fontSize: 13, background: '#FFFFFF', outline: 'none', boxSizing: 'border-box', color: item.manualPrice ? '#0F6E56' : '#03045E', fontWeight: item.manualPrice ? 600 : 400 }} step="0.01" />
                            {item.manualPrice && (
                              <>
                                <span title="Manual price — won't change with price level" className="text-amber-500 text-xs">✏️</span>
                                <button onClick={() => resetItemPrice(item.id)}
                                  title="Reset to price level pricing"
                                  className="text-gray-400 hover:text-gray-700 text-sm leading-none px-0 py-0 bg-transparent border-none cursor-pointer"
                                >↺</button>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right font-bold" style={{ color: item.isReturnCredit ? '#A32D2D' : '#0077B6' }}>
                          {item.isReturnCredit ? '-' : ''}${Math.abs(lineTotal(item)).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button onClick={() => removeLineItem(item.id)} style={{ background: 'transparent', border: 'none', color: '#A32D2D', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>✕</button>
                        </td>
                      </tr>
                    );
                  });
                  return rows;
                })()}
              </tbody>
            </table>
          )}
        </div>

        {/* Delivery + Notes — two side-by-side cards (responsive: stack when narrow) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16, marginBottom: 16 }}>
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20 }}>
          <label htmlFor="isDelivery" style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 16 }}>
            <input type="checkbox" id="isDelivery" checked={isDelivery}
              onChange={(e) => setIsDelivery(e.target.checked)}
              style={{ width: 18, height: 18, accentColor: '#0077B6', cursor: 'pointer' }} />
            <span style={{ color: '#03045E', fontWeight: 700, fontSize: 14 }}>🚚 Delivery Order</span>
          </label>
          {isDelivery && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ color: '#6B7C93', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>Delivery Fee ($)</label>
                <input type="number" step="0.01" value={deliveryFee}
                  onChange={(e) => setDeliveryFee(e.target.value)}
                  placeholder="0.00"
                  onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                  style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }} />
              </div>
              <div>
                <label style={{ color: '#6B7C93', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>Delivery Address</label>
                <input type="text" value={deliveryStreet}
                  onChange={(e) => setDeliveryStreet(e.target.value)}
                  placeholder="Street Address"
                  onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                  style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s', marginBottom: 8 }} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  <input type="text" value={deliveryCity}
                    onChange={(e) => setDeliveryCity(e.target.value)}
                    placeholder="City"
                    onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                    onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                    style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }} />
                  <input type="text" value={deliveryState}
                    onChange={(e) => setDeliveryState(e.target.value)}
                    placeholder="State"
                    onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                    onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                    style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }} />
                  <input type="text" value={deliveryZip}
                    onChange={(e) => setDeliveryZip(e.target.value)}
                    placeholder="Zip Code"
                    onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                    onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
                    style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s' }} />
                </div>
              </div>
            </div>
          )}
          </div>
          <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20 }}>
            <label style={{ color: '#6B7C93', fontSize: 11, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>📝 Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              rows="3" placeholder="Additional notes..."
              onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
              onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }}
              style={{ width: '100%', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 10, color: '#03045E', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.3s, box-shadow 0.3s', resize: 'vertical', minHeight: 80 }} />
          </div>
        </div>

        {/* Totals */}
        <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 24, marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
            <input type="checkbox" checked={docTaxExempt} onChange={e => setDocTaxExempt(e.target.checked)} style={{ accentColor: '#0077B6', width: 18, height: 18 }} />
            <span style={{ color: '#03045E', fontSize: 14, fontWeight: 600 }}>Tax Exempt (this document)</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
            <input type="checkbox" checked={discountEnabled}
              onChange={e => { const on = e.target.checked; setDiscountEnabled(on); if (!on) setDiscountPercent(0); }}
              style={{ accentColor: '#0077B6', width: 18, height: 18 }} />
            <span style={{ color: '#03045E', fontSize: 14, fontWeight: 600 }}>Discount</span>
          </label>
          {discountEnabled && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10, marginBottom: 14 }}>
              <span style={{ color: '#03045E', fontSize: 14, fontWeight: 600, flex: '0 0 auto' }}>Discount (%)</span>
              <input type="number" min="0" max="100" step="0.1" value={discountPercent}
                onChange={e => setDiscountPercent(e.target.value)}
                style={{ flex: '0 0 90px', width: 90, maxWidth: 90, padding: '8px 10px', background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 8, color: '#03045E', fontSize: 14, outline: 'none', textAlign: 'right', boxSizing: 'border-box', fontFamily: 'inherit' }}
                onFocus={e => { e.target.style.borderColor = '#0077B6'; e.target.style.boxShadow = '0 0 0 3px rgba(0,119,182,0.12)'; }}
                onBlur={e => { e.target.style.borderColor = '#E3ECF5'; e.target.style.boxShadow = 'none'; }} />
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: '#6B7C93', fontSize: 14 }}>Subtotal</span>
            <span style={{ color: '#03045E', fontWeight: 600, fontSize: 15 }}>${subtotal.toFixed(2)}</span>
          </div>
          {discountAmount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: '#6B7C93', fontSize: 14 }}>Discount ({parseFloat(discountPercent) || 0}%)</span>
              <span style={{ color: '#A32D2D', fontWeight: 600, fontSize: 15 }}>−${discountAmount.toFixed(2)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ color: '#6B7C93', fontSize: 14 }}>
              Sales Tax (6% MD) {docTaxExempt && <span style={{ color: '#0F6E56', fontWeight: 700 }}>— EXEMPT</span>}
            </span>
            <span style={{ color: '#03045E', fontWeight: 600, fontSize: 15 }}>${tax.toFixed(2)}</span>
          </div>
          {isDelivery && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: '#6B7C93', fontSize: 14 }}>Delivery Fee</span>
              <span style={{ color: '#03045E', fontWeight: 600, fontSize: 15 }}>${delivery.toFixed(2)}</span>
            </div>
          )}
          <div style={{ marginTop: 14, padding: '14px 16px', background: '#E6F1FB', borderRadius: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#03045E', fontSize: 14, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700 }}>Grand Total</span>
            <span style={{ fontSize: 36, fontWeight: 800, lineHeight: 1, color: '#0077B6' }}>${grandTotal.toFixed(2)}</span>
          </div>
        </div>

        {/* Payment Section — invoices only */}
        {docType === 'invoice' && (
          <div
            style={{
              background: '#EEEDFE',
              border: '1px solid #CECBF6',
              borderLeft: `4px solid ${
                !paymentsLoaded ? '#C99A33'
                  : payStatus.status === PAYMENT_STATUS.CREDIT ? '#0077B6'
                  : payStatus.status === PAYMENT_STATUS.PAID ? '#0F6E56'
                  : payStatus.status === PAYMENT_STATUS.PARTIAL ? '#854F0B'
                  : '#A32D2D'}`,
              borderRadius: 16, padding: 24, marginBottom: 16,
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <h3 style={{ color: '#3C3489', fontWeight: 700, fontSize: 18, margin: 0 }}>💳 Payment</h3>
              {!paymentsLoaded ? (
                <span style={{ background: '#FDF1DD', color: '#854F0B', fontWeight: 700, padding: '4px 12px', borderRadius: 999, fontSize: 13 }}>{paymentsError ? 'NOT LOADED ⚠️' : 'CHECKING…'}</span>
              ) : payStatus.status === PAYMENT_STATUS.CREDIT ? (
                <span style={{ background: '#E6F1FB', color: '#0077B6', fontWeight: 700, padding: '4px 12px', borderRadius: 999, fontSize: 13 }}>CREDIT 💙</span>
              ) : payStatus.status === PAYMENT_STATUS.PAID ? (
                <span style={{ background: '#E6F7EE', color: '#0F6E56', fontWeight: 700, padding: '4px 12px', borderRadius: 999, fontSize: 13 }}>PAID IN FULL ✅</span>
              ) : payStatus.status === PAYMENT_STATUS.PARTIAL ? (
                <span style={{ background: '#FDF1DD', color: '#854F0B', fontWeight: 700, padding: '4px 12px', borderRadius: 999, fontSize: 13 }}>PARTIAL 🟡</span>
              ) : (
                <span style={{ background: '#FBECEC', color: '#A32D2D', fontWeight: 700, padding: '4px 12px', borderRadius: 999, fontSize: 13 }}>UNPAID 🔴</span>
              )}
            </div>

            {!paymentsLoaded && (
              <p style={{ color: '#854F0B', fontSize: 13, fontWeight: 600, margin: '0 0 8px 0' }}>
                {paymentsError
                  ? "⚠️ This invoice's payment records could not be loaded, so its status is not known. Save will try to read them again, and will refuse to save if it still cannot."
                  : 'Checking the payment records…'}
              </p>
            )}

            {/* Recorded payments list */}
            {paymentsLoaded && payments.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 4 }}>
                {payments.map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFFFFF', border: '1px solid #CECBF6', borderRadius: 10, padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, flexWrap: 'wrap' }}>
                      <span style={{ color: '#26215C', fontWeight: 700 }}>
                        {p.method === 'Cash' ? '💵' : p.method === 'Card' ? '💳' : '🏦'}{' '}
                        {p.method}
                      </span>
                      <span style={{ color: '#26215C', fontWeight: 700 }}>${parseFloat(p.amount || 0).toFixed(2)}</span>
                      <span style={{ color: '#6A6391' }}>{new Date(p.date + 'T12:00:00').toLocaleDateString('en-US')}</span>
                      {p.method === 'Check' && p.checkNumber && (
                        <span style={{ color: '#6A6391', fontSize: 12 }}>Check #{p.checkNumber}</span>
                      )}
                      {p.notes && <span style={{ color: '#6A6391', fontSize: 12, fontStyle: 'italic' }}>{p.notes}</span>}
                    </div>
                  </div>
                ))}

                {/* Summary row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 10, marginTop: 4, borderTop: '1px solid #CECBF6' }}>
                  <span style={{ color: '#0F6E56', fontWeight: 700, fontSize: 14 }}>Total Paid: ${totalPaid.toFixed(2)}</span>
                  {balanceDue > 0 && (
                    <span style={{ color: '#A32D2D', fontWeight: 700, fontSize: 14 }}>Balance Due: ${balanceDue.toFixed(2)}</span>
                  )}
                  {creditAmount > 0 && (
                    <span style={{ color: '#0077B6', fontWeight: 700, fontSize: 14 }}>Credit: ${creditAmount.toFixed(2)} overpaid</span>
                  )}
                </div>
              </div>
            )}

            {/* Payments are managed only in ViewDocument (record + delete) — this
                section is READ-ONLY: status badge, payment history, and totals. */}
          </div>
        )}

        {/* Save */}
        <div style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', borderRadius: 16, padding: 20, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={handleSave} disabled={saving || saved || !selectedCustomer || lineItems.length === 0}
              style={{ background: '#0077B6', border: 'none', color: '#fff', fontWeight: 700, padding: '12px 28px', borderRadius: 12, fontSize: 16, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,119,182,0.3)', opacity: (saving || saved || !selectedCustomer || lineItems.length === 0) ? 0.6 : 1 }}>
              {saving ? 'Saving...' : saved ? '✅ Saved!' : '💾 Save Changes'}
            </button>
            <button onClick={() => confirmLeave('/dashboard')}
              style={{ background: '#FFFFFF', border: '1px solid #E3ECF5', color: '#03045E', fontWeight: 600, padding: '12px 28px', borderRadius: 12, fontSize: 16, cursor: 'pointer' }}>
              ← Dashboard
            </button>
            {docType === 'invoice' && (
              <button onClick={handleDowngradeToQuote}
                title={`Convert this ${wordTitle.toLowerCase()} back to a quote`}
                style={{ background: '#FDF1DD', border: '1px solid #E6C998', color: '#854F0B', fontWeight: 700, padding: '12px 28px', borderRadius: 12, fontSize: 16, cursor: 'pointer' }}>
                ⬇️ Downgrade to Quote
              </button>
            )}
            {docType === 'quote' && (
              originalDoc?.convertedToInvoice ? (
                <button onClick={handleConvertToInvoice}
                  title={`Already converted — open invoice #${originalDoc.convertedToInvoice}`}
                  style={{ background: '#E6F1FB', border: '1px solid #E3ECF5', color: '#6B7C93', fontWeight: 700, padding: '12px 28px', borderRadius: 12, fontSize: 16, cursor: 'pointer' }}>
                  ✓ Converted → #{originalDoc.convertedToInvoice}
                </button>
              ) : (
                <button onClick={handleConvertToInvoice}
                  disabled={converting || saving || !selectedCustomer || lineItems.length === 0}
                  title={`Save edits and convert this quote to ${wordTitle.toLowerCase()}`}
                  style={{ background: '#E6F7EE', border: '1px solid #0F6E56', color: '#0F6E56', fontWeight: 700, padding: '12px 28px', borderRadius: 12, fontSize: 16, cursor: (converting || saving || !selectedCustomer || lineItems.length === 0) ? 'default' : 'pointer', opacity: (converting || saving || !selectedCustomer || lineItems.length === 0) ? 0.6 : 1 }}>
                  {converting ? 'Converting...' : `🧾 Convert to ${wordTitle}`}
                </button>
              )
            )}
          </div>
          {(!selectedCustomer || lineItems.length === 0) && !saved && (
            <p style={{ color: '#6B7C93', fontSize: 13, margin: 0 }}>Add a customer and at least one item to save</p>
          )}
        </div>
      </div>
      <TemplatesPickerModal
        open={showTemplatesPicker}
        onClose={() => setShowTemplatesPicker(false)}
        onSelectCustomTemplate={addTemplateBundleToDocument}
        onSelectStockBundle={addStockBundleToDocument}
      />

      <LineItemEditorModal
        open={showItemEditor}
        item={editingItem}
        onClose={() => { setShowItemEditor(false); setEditingItem(null); }}
        onSave={saveItemEdits}
        products={products}
        globalPriceLevel={priceLevel}
      />

      <ProductPickerModal
        open={showProductPicker}
        onClose={() => setShowProductPicker(false)}
        onAdd={addProductsFromPicker}
        products={products}
        categories={CATEGORIES}
        currentPriceLevel={priceLevel}
      />

      {/* Single-item inventory alert */}
      {stockAlertProduct && stockAlertStatus && (() => {
        const isOut = stockAlertStatus.level === 'out';
        const title = stockAlertStatus.level === 'out' ? '🔴 Out of Stock'
          : stockAlertStatus.level === 'insufficient' ? '⚠️ Not Enough Stock'
          : '🟡 Low Stock';
        const closeAlert = () => { setStockAlertProduct(null); setStockAlertStatus(null); };
        return (
          <div className="fixed inset-0 bg-black bg-opacity-70 z-[80] flex items-center justify-center p-4 print:hidden">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <h3 className={`text-xl font-extrabold mb-4 ${isOut ? 'text-red-600' : 'text-amber-600'}`}>{title}</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-5">
                <p className="font-bold text-gray-800 text-base">{stockAlertProduct.description}</p>
                <p className="text-gray-500 text-xs mt-1">
                  <span className="font-mono font-bold text-blue-600">{stockAlertProduct.sku}</span>
                  {stockAlertProduct.category ? ` · ${stockAlertProduct.category}` : ''}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className={`rounded-xl p-4 text-center border ${isOut ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                  <p className="text-gray-500 text-xs uppercase tracking-wide font-bold">Currently in Stock</p>
                  <p className={`text-3xl font-extrabold mt-1 leading-none ${isOut ? 'text-red-600' : 'text-amber-600'}`}>{stockAlertStatus.stockQty}</p>
                  <p className="text-gray-400 text-xs mt-1">{stockAlertProduct.unit}</p>
                </div>
                <div className="rounded-xl p-4 text-center border bg-blue-50 border-blue-200">
                  <p className="text-gray-500 text-xs uppercase tracking-wide font-bold">Reorder Point</p>
                  <p className="text-3xl font-extrabold mt-1 leading-none text-blue-600">{parseFloat(stockAlertProduct.reorderPoint) || 0}</p>
                  <p className="text-gray-400 text-xs mt-1">{stockAlertProduct.unit}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => { const p = stockAlertProduct; closeAlert(); commitLineItem(p); }}
                  className="flex-1 bg-blue-700 hover:bg-blue-800 text-white font-bold py-3 rounded-xl transition">✓ Add Anyway</button>
                <button onClick={closeAlert}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-xl transition">Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Batch inventory alert (from the Add Products picker) */}
      {batchStockAlerts && batchStockAlerts.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-[80] flex items-center justify-center p-4 print:hidden">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 flex flex-col" style={{ maxHeight: '80vh' }}>
            <h3 className="text-amber-600 text-xl font-extrabold mb-1">⚠️ Stock Alerts</h3>
            <p className="text-gray-500 text-sm mb-4">Some of the selected products have stock issues. Review before adding.</p>
            <div className="flex-1 overflow-y-auto mb-5 flex flex-col gap-2">
              {batchStockAlerts.map(({ product, status }) => {
                const isOut = status.level === 'out';
                return (
                  <div key={product.id} className={`rounded-lg px-4 py-3 flex items-center justify-between gap-3 border ${isOut ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-800 font-bold text-sm">{product.description}</p>
                      <p className="text-gray-500 text-xs mt-0.5"><span className="font-mono text-blue-600">{product.sku}</span></p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`text-xs font-bold uppercase ${isOut ? 'text-red-600' : 'text-amber-600'}`}>{status.message}</p>
                      <p className="text-gray-800 text-base font-bold mt-0.5">{status.stockQty} {product.unit} left</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-3">
              <button onClick={() => commitProductsFromPicker(pendingBatchProducts)}
                className="flex-1 bg-blue-700 hover:bg-blue-800 text-white font-bold py-3 rounded-xl transition">✓ Add All Anyway</button>
              <button onClick={() => { setBatchStockAlerts([]); setPendingBatchProducts(null); }}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-xl transition">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Quick New Customer modal — create without leaving the document */}
      {showNewCustomerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 print:hidden"
          onClick={() => !creatingCustomer && setShowNewCustomerModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-gray-800 text-xl">+ New Customer</h3>
              <button onClick={() => setShowNewCustomerModal(false)} disabled={creatingCustomer}
                className="text-gray-400 hover:text-gray-600 font-bold text-xl leading-none disabled:opacity-50">✕</button>
            </div>
            <p className="text-gray-500 text-sm mb-5">
              Create a new customer for this {docType === 'quote' ? 'quote' : wordTitle.toLowerCase()}. They'll be added to your customer database.
            </p>

            <div className="mb-3">
              <label className="block text-gray-700 font-semibold text-sm mb-1">Full Name *</label>
              <input type="text" value={newCustomerData.name} autoFocus
                onChange={e => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                onKeyDown={e => { if (e.key === 'Enter' && newCustomerData.name.trim() && !creatingCustomer) handleCreateAndSelectCustomer(); }}
                placeholder="John Smith"
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-600" />
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-gray-700 font-semibold text-sm mb-1">Phone</label>
                <input type="tel" value={newCustomerData.phone}
                  onChange={e => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
                  placeholder="410-555-0123"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-600" />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold text-sm mb-1">Email</label>
                <input type="email" value={newCustomerData.email}
                  onChange={e => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                  placeholder="optional"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-600" />
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-gray-700 font-semibold text-sm mb-1">Address</label>
              <input type="text" value={newCustomerData.address}
                onChange={e => setNewCustomerData({ ...newCustomerData, address: e.target.value })}
                placeholder="123 Main St, Hanover MD"
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-600" />
            </div>

            <div className="mb-3">
              <label className="block text-gray-700 font-semibold text-sm mb-1">Notes (optional)</label>
              <textarea value={newCustomerData.notes}
                onChange={e => setNewCustomerData({ ...newCustomerData, notes: e.target.value })}
                placeholder="Any notes about this customer..."
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:border-blue-600 min-h-[60px] resize-y" />
            </div>

            <label className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer mb-5">
              <input type="checkbox" checked={newCustomerData.taxExempt}
                onChange={e => setNewCustomerData({ ...newCustomerData, taxExempt: e.target.checked })}
                className="w-4 h-4 accent-blue-600" />
              <span className="text-gray-800 text-sm font-semibold">Tax Exempt Customer</span>
            </label>

            <div className="flex gap-3">
              <button onClick={handleCreateAndSelectCustomer}
                disabled={creatingCustomer || !newCustomerData.name.trim()}
                className="flex-1 bg-blue-700 hover:bg-blue-800 text-white font-bold py-2.5 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed">
                {creatingCustomer ? 'Creating...' : '✓ Create & Select'}
              </button>
              <button onClick={() => setShowNewCustomerModal(false)} disabled={creatingCustomer}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2.5 rounded-xl transition disabled:opacity-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Possible-duplicate warning modal */}
      {showDuplicateWarning && possibleDuplicates.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4 print:hidden">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 flex flex-col" style={{ maxHeight: '85vh' }}>
            <h3 className="text-amber-600 text-xl font-extrabold mb-1">⚠️ Possible Duplicate Customers</h3>
            <p className="text-gray-500 text-sm mb-5">
              We found existing customers that match the information you entered. Please verify if any of these are the same person.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
              <p className="text-gray-500 text-xs uppercase tracking-wide font-bold m-0">You're creating:</p>
              <p className="text-gray-800 font-bold text-base mt-1 mb-0.5">{pendingCustomerData?.name}</p>
              <p className="text-gray-500 text-xs m-0">
                {pendingCustomerData?.phone || 'No phone'}{pendingCustomerData?.email && ` · ${pendingCustomerData.email}`}
              </p>
            </div>

            <p className="text-gray-500 text-xs uppercase tracking-wide font-bold mb-2">Existing customers found:</p>

            <div className="flex-1 overflow-y-auto mb-5 flex flex-col gap-2">
              {possibleDuplicates.map(dup => (
                <div key={dup.id} className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <p className="text-gray-800 font-bold text-sm m-0">{dup.name}</p>
                      <span className="text-violet-700 font-mono text-xs font-bold bg-violet-100 px-2 py-0.5 rounded">{dup.customerNumber}</span>
                      {dup.matchType === 'exact-name' && (
                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-bold">EXACT NAME</span>
                      )}
                      {(dup.matchType === 'similar-name-same-phone' || dup.matchType === 'same-phone') && (
                        <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs font-bold">SAME PHONE</span>
                      )}
                      {dup.matchType === 'similar-name-same-email' && (
                        <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs font-bold">SAME EMAIL</span>
                      )}
                    </div>
                    <p className="text-gray-500 text-xs m-0">
                      📞 {dup.phone || 'No phone'}{dup.email && ` · ✉️ ${dup.email}`}
                    </p>
                    {dup.address && <p className="text-gray-400 text-xs mt-0.5 mb-0">📍 {dup.address}</p>}
                  </div>
                  <button onClick={() => selectExistingCustomer(dup)}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold px-3.5 py-2 rounded-lg text-xs whitespace-nowrap flex-shrink-0 transition">
                    ✓ Use This
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button onClick={createAnyway} disabled={creatingCustomer}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed">
                {creatingCustomer ? 'Creating...' : '+ Create Anyway as New Customer'}
              </button>
              <button onClick={() => { setShowDuplicateWarning(false); setPossibleDuplicates([]); setPendingCustomerData(null); }}
                disabled={creatingCustomer}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-xl transition disabled:opacity-50">
                ← Back to Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Downgrade invoice → quote */}
      {showDowngradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-[80] flex items-center justify-center p-4 print:hidden">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <h3 className="text-amber-600 text-xl font-extrabold mb-2">⬇️ Downgrade to Quote</h3>

            {downgradeStep === 'confirm' && (
              <>
                <p className="text-gray-600 text-sm mb-4">
                  This will convert {wordTitle.toLowerCase()} <strong className="font-mono text-blue-700">#{originalDoc?.docNumber || docNumber}</strong> back into a quote.
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
                  <p className="text-gray-500 text-xs uppercase tracking-wide font-bold mb-2">What will happen:</p>
                  <ul className="text-gray-700 text-xs list-disc pl-5 space-y-1">
                    <li>A new quote will be created with a new quote number</li>
                    <li>All line items, customer, notes, and settings are preserved</li>
                    <li>The board status is cleared</li>
                    <li>The original {wordTitle.toLowerCase()} is deleted</li>
                    {downgradeInventoryItems.length > 0 && (
                      <li>You'll be asked whether to return {downgradeInventoryItems.length} item(s) to inventory</li>
                    )}
                  </ul>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { if (downgradeInventoryItems.length > 0) setDowngradeStep('inventory'); else performDowngrade(false); }}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 rounded-xl transition">
                    Continue →
                  </button>
                  <button onClick={() => setShowDowngradeModal(false)}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-xl transition">
                    Cancel
                  </button>
                </div>
              </>
            )}

            {downgradeStep === 'inventory' && (
              <>
                <p className="text-gray-600 text-sm mb-3">Return these items to inventory?</p>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-3 max-h-52 overflow-y-auto">
                  {downgradeInventoryItems.map(item => (
                    <div key={item.productId} className="flex justify-between py-1.5 border-b border-gray-100 text-xs last:border-0">
                      <span className="text-gray-800">{item.description}</span>
                      <span className="text-green-600 font-bold">+{item.qty}</span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-400 text-xs mb-4 leading-relaxed">
                  Returning items adds their quantities back to current stock. If inventory was never deducted for this {wordTitle.toLowerCase()}, skip this step.
                </p>
                <div className="flex gap-3">
                  <button onClick={() => performDowngrade(true)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-xl transition">
                    ✓ Return &amp; Downgrade
                  </button>
                  <button onClick={() => performDowngrade(false)}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-xl transition">
                    Skip &amp; Downgrade
                  </button>
                </div>
              </>
            )}

            {downgradeStep === 'processing' && (
              <div className="text-center py-6">
                <p className="text-gray-800 text-base font-semibold">Processing downgrade...</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Thin wrapper: key the inner component on the route's type/id so navigating
// between documents (e.g. converting a quote → invoice, which changes the id)
// forces a clean remount instead of reusing the instance with stale state.
// This resets all state/refs exactly like reopening the document.
function EditDocument(props) {
  const { type, id } = useParams();
  return <EditDocumentInner key={`${type}/${id}`} {...props} />;
}

export default EditDocument;
